/*!
 * Portal JS
 */

export async function runPortalGeneral (deps = {}) {
const $ = deps.$
const _ = deps._
const L = deps.L
const {
    clamp,
    offsetLatLngByMeters,
    bearingBetweenLatLng,
    angleDiffDeg,
    axialDiffDeg,
    normalizeBearingDeg,
    roundToTenth,
    toFiniteNumber,
    interpolateByRatioNullable,
    pathWithoutNamespacePrefixes,
    pathWithoutLeadingScope,
    pathWithoutNumericParts,
    getPathLeafField,
    toUnixMs,
    formatUnixMsForDisplay,
    parseDisplayTimestampToUnixMs,
    upsertDocumentWpmlTimestamp,
    readDocumentWpmlTimestampMs,
    getMissionCentroidLatLng,
    normalizeMissionBaseName,
    fetchNearestSettlementName,
    buildLocationBasedMissionBaseName,
    buildLayerStyle,
    parseKmlMission,
    ensureTooltip,
    ensureDropdown,
    applyMod2ShortcutTooltips,
    isTypingContext,
    parseShortcut,
    normalizeEventKey,
    eventMatchesShortcut,
    renderMarkdownToHtml,
    renderReadmePanel,
    I18N_HU,
    PORTAL_PRESETS,
    DEM_PROVIDER_CONFIG = {},
    DEM_PROVIDER_PRIORITY = [],
    DEM_CURSOR_PROVIDER_PRIORITY = [],
    DEM_PROVIDER_G = 'gDEM',
    DEFAULT_MAP_CURSOR_ELEVATION_PROVIDER = 'gDEM',
    fnIsDemProviderEnabled = function () { return true },
    MAP_CURSOR_ELEVATION_CACHE_MAX = 500,
    MAP_CURSOR_ELEVATION_COORD_DECIMALS = 4,
    MAP_CURSOR_ELEVATION_MIN_REQUEST_INTERVAL_MS = 250,
    fnGetKmlActionsFromProps,
    fnIsKmlActionPath,
    fnSetKmlActionsInProps,
    fnSetPointKmlActions,
    fnBuildSharedKmlActionsForPoints,
    fnGetKmzActionGroupsFromProps,
    fnIsKmzActionPath,
    fnSetKmzActionGroupsInProps,
    fnSetPointKmzActionGroups,
    fnApplyKmzActionFileSuffixDisplayModeToGroups,
    fnResolveKmzActionFileSuffixPointNameTokensInGroups,
    fnSyncPointActionFileSuffixWithName,
    fnBuildSharedKmzActionGroupsForPoints,
    fnToBoolLike,
    normalizeKmzWaypointForExport,
    normalizeKmzMissionLevelForExport,
    buildKmzCanonicalExportDocs,
    parseKmzMission,
    renderPortalPropEditorRows,
    createPortalPropRuntime,
    buildRenderPropEditorRowsDeps,
    buildPointEditLauncherOptions,
    createPortalPointEditLaunchers,
    showPortalPropContextMenu,
    portalAltitudeUtils,
    portalPointMapUtils,
    portalImportStateUtils,
    portalWaypointCoreUtils,
    renderImportLinesRaw,
    setMissionWaypointLatLngRaw,
    placeMissionWaypointsInViewportRaw,
    loadMissionFromParsedMissionRaw,
    closeEditedMissionRaw,
    closeMissionVariantsRaw,
    restoreImportDraftFromIndexedDbRaw,
    exportCurrentMissionRaw,
    escapeHtmlLite,
    runPointHeightDemOverwriteFlow,
    cacheDataKey,
    refreshGeoTiffCacheMaxBytes,
    cacheReadIndex,
    cacheWriteIndex,
    cacheTouch,
    cacheLoad,
    cacheStore,
    loadGeoTiffFile,
    openMapCursorElevationSettingsModal,
    openProjectSettingsEditor,
    bindCoreUiHandlers,
    createRoute3dSyncController,
    createPortalInfoController,
    bindInfoModalHandler,
    createSeekTools,
    bindMissionToolbarHandlers,
    bindWaypointToolsHandlers,
    createLayerParsers,
    createLayerLoaderController,
    createLayerCoreController,
    initializePortalMap,
    createMyLocationController,
    createMapCursorElevationController,
    createDemBatchController,
    fetchSurfaceElevationByProvider,
    bindSeekUiHandlers,
    deactivateMod2ModesUi,
    initMod2KeyboardShortcutsUi,
    initMobileNavbarArrowsUi,
    bindPolygonCreateClick,
    bindAltitudeUiHandlers,
    bindQuickActionHandlers,
    bindExternalLinkInterceptor,
    bindBootstrapOverlayUiHandlers,
    bindBeforeUnloadCleanupHandler,
} = deps


const $body = $('body')
const fnT = function (key, fallback) {
  const k = String(key || '').trim()
  if (!k) return String(fallback || '')
  if (Object.prototype.hasOwnProperty.call(I18N_HU, k)) return String(I18N_HU[k])
  return String(fallback || k)
}
const fnTF = function (key, vars, fallback) {
  const text = fnT(key, fallback)
  const data = (vars && typeof vars === 'object') ? vars : {}
  return text.replace(/\{([a-zA-Z0-9_]+)\}/g, function (_m, p1) {
    return Object.prototype.hasOwnProperty.call(data, p1) ? String(data[p1]) : ''
  })
}
const fnResolveI18Text = function (rawText, fallback) {
  const raw = String(rawText || '').trim()
  if (!raw) return String(fallback || '')
  const likelyKey = /^[a-z0-9_.-]+$/i.test(raw)
  if (!likelyKey) return raw
  const missingSentinel = '__PORTAL_I18N_MISSING__'
  const translated = fnT(raw, missingSentinel)
  if (translated !== missingSentinel) return translated
  return String(fallback || '')
}

const DB_STORE_KEY = 'portal-db'
const fnCloneDbState = function (value) {
  if (!value || typeof value !== 'object') return {}
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (_error) {
    return {}
  }
}
const fnReadLegacyDbState = function () {
  const legacyDb = {}
  try {
    if (!window.localStorage) return legacyDb
    const legacySettingsRaw = window.localStorage.getItem('settings')
    if (legacySettingsRaw === null || legacySettingsRaw === undefined) return legacyDb
    try {
      legacyDb.settings = JSON.parse(legacySettingsRaw)
    } catch (_parseError) {
      legacyDb.settings = legacySettingsRaw
    }
  } catch (_error) {}
  return legacyDb
}
const store = {
  async getAll () {
    try {
      const stored = await window.idbKeyval.get(DB_STORE_KEY)
      const safeStored = fnCloneDbState(stored)
      if (Object.keys(safeStored).length) {
        return safeStored
      }
      const legacyDb = fnReadLegacyDbState()
      if (Object.keys(legacyDb).length) {
        void this.setAll(legacyDb)
        return legacyDb
      }
      return safeStored
    } catch (error) {
      console.error(fnT('log.indexeddb_getall_error'), error)
      return {}
    }
  },
  async setAll (nextDb) {
    try {
      await window.idbKeyval.set(DB_STORE_KEY, fnCloneDbState(nextDb))
    } catch (error) {
      console.error(fnT('log.indexeddb_setall_error'), error)
    }
  }
}
const db = await store.getAll()
let settingsChanged = false
if (!db.settings) {
  settingsChanged = true
    db.settings = {
      overlayActive: [],
      overlayVisible: {
        notam: true,
        drawn: true,
        import: true,
        tiffDem: true,
        tiffOrtho: true
      },
      baseMap: null,
      center: null,
      zoom: null,
      mapCursorElevation: {
        provider: DEFAULT_MAP_CURSOR_ELEVATION_PROVIDER
      }
  }
}
if (!db.settings.overlayVisible) {
  settingsChanged = true
  db.settings.overlayVisible = {
    notam: true,
    drawn: true,
    import: true,
    tiffDem: true,
    tiffOrtho: true
  }
}
if (typeof db.settings.overlayVisible.import === 'undefined' && typeof db.settings.overlayVisible.wayline !== 'undefined') {
  settingsChanged = true
  db.settings.overlayVisible.import = db.settings.overlayVisible.wayline
}
if (typeof db.settings.overlayVisible.tiffDem === 'undefined') {
  settingsChanged = true
  db.settings.overlayVisible.tiffDem = (typeof db.settings.overlayVisible.tiff === 'undefined') ? true : (db.settings.overlayVisible.tiff !== false)
}
if (typeof db.settings.overlayVisible.tiffOrtho === 'undefined') {
  settingsChanged = true
  db.settings.overlayVisible.tiffOrtho = (typeof db.settings.overlayVisible.tiff === 'undefined') ? true : (db.settings.overlayVisible.tiff !== false)
}
if (typeof db.settings.tiffResolutionTerrain === 'undefined') {
  const legacyResolution = Number(db.settings.tiffResolution)
  if (Number.isFinite(legacyResolution) && legacyResolution > 0) {
    settingsChanged = true
    db.settings.tiffResolutionTerrain = legacyResolution
  }
}
if (typeof db.settings.tiffResolutionOrtho === 'undefined') {
  const legacyResolution = Number(db.settings.tiffResolution)
  if (Number.isFinite(legacyResolution) && legacyResolution > 0) {
    settingsChanged = true
    db.settings.tiffResolutionOrtho = legacyResolution
  }
}
if (!db.settings.mapCursorElevation || typeof db.settings.mapCursorElevation !== 'object') {
  settingsChanged = true
  db.settings.mapCursorElevation = { provider: DEFAULT_MAP_CURSOR_ELEVATION_PROVIDER }
}
if (!String(db.settings.mapCursorElevation.provider || '').trim()) {
  settingsChanged = true
  db.settings.mapCursorElevation.provider = DEFAULT_MAP_CURSOR_ELEVATION_PROVIDER
}
if (settingsChanged) {
  void store.setAll(db)
}
console.log('db', db)

const $leafletmap = $('[data-leafletmap]')

const baseSwal = Swal.mixin({
  position: 'bottom-right',
  backdrop: false,
  title: fnT('common.warning'),
  customClass: {popup: 'swal-toast-bottom-center'}
})

const PROP_SORT_PREFIX_PRIORITY = [
  'name',
  'description',
  'ExtendedData',
  'coordinates',
  'Point.coordinates',
  'Point'
]
const NON_EXPORT_PROJECT_FILENAME_PATH = 'project.filename'
const NON_EXPORT_POINT_HEIGHT_PATH = 'project.height'
const WPML_NAMESPACE_URI = 'http://www.dji.com/wpmz/1.0.6'
const PORTAL_MOD2_SHORTCUTS = [
  { selector: '[data-leaf-undo]', shortcut: 'Ctrl+Z' },
  { selector: '[data-leaf-redo]', shortcut: 'Ctrl+Shift+Z' },
  { selector: '[data-leaf-project-settings]', shortcut: 'F2' },
  { selector: '[data-leaf-export-kmz]', shortcut: 'E' },
  { selector: '[data-leaf-route-3d]', shortcut: '3' },
  { selector: '[data-leaf-center]', shortcut: 'C' },
  { selector: '[data-leaf-select-poly]', shortcut: 'W' },
  { selector: '[data-leaf-select-all]', shortcut: 'A' },
  { selector: '[data-leaf-delete-selected]', shortcut: 'Delete' },
  { selector: '[data-leaf-add-point]', shortcut: 'P' },
  { selector: '[data-leaf-divide-points]', shortcut: 'D' },
  { selector: '[data-leaf-edit-selected]', shortcut: 'T' },
  { selector: '[data-leaf-seek]', shortcut: 'S' },
  { selector: '[data-leaf-altitude]', shortcut: 'H' }
]
const {
  propFieldDisableRules: PROP_FIELD_DISABLE_RULES,
  altitudeRules: ALTITUDE_RULES,
  pointAltitudeModeValues: POINT_ALTITUDE_MODE_VALUES,
  fnResolvePropPresetOptions,
  fnResolvePropLabelTooltip,
  fnNormalizeIconClass,
  fnResolvePropLabelIconClass,
  fnResolvePropLabelRequired,
  fnResolvePropLabelDetailedHelp,
  fnResolvePropValueHelp
} = createPortalPropRuntime(PORTAL_PRESETS, {
  _,
  fnT,
  fnTF,
  fnResolveI18Text,
  pathWithoutLeadingScope,
  pathWithoutNumericParts,
  getPathLeafField
})
const OPEN_ELEVATION_API_URL = 'https://api.open-elevation.com/api/v1/lookup'
const GOOGLE_ELEVATION_API_URL = 'https://maps.googleapis.com/maps/api/elevation/json'
const fnGetGoogleElevationApiKey = function () {
  const fromBodyData = String($body.data('googleMapsApiKey') || '').trim()
  if (fromBodyData) return fromBodyData
  const fromBodyAttr = String($body.attr('data-google-maps-api-key') || '').trim()
  if (fromBodyAttr) return fromBodyAttr
  const fromWindow = String(window.GOOGLE_ELEVATION_API_KEY || '').trim()
  if (fromWindow) return fromWindow
  return ''
}
const googleElevationApiKey = fnGetGoogleElevationApiKey()
const OPENTOPODATA_API_URL = 'https://api.opentopodata.org/v1/srtm90m'
const OPEN_METEO_ELEVATION_API_URL = 'https://api.open-meteo.com/v1/elevation'
const TIMESTAMP_DISPLAY_FORMAT = 'YYYY-MM-DD HH:mm:ss'
let kmzUpdateTimeTouchTimer = null
let kmzUpdateTimeTouchLock = false

const fnFormatUnixMsForDisplay = function (rawValue) {
  return formatUnixMsForDisplay(rawValue, TIMESTAMP_DISPLAY_FORMAT, moment)
}
const fnParseDisplayTimestampToUnixMs = function (rawText) {
  return parseDisplayTimestampToUnixMs(rawText, TIMESTAMP_DISPLAY_FORMAT, moment)
}
const fnUpsertDocumentWpmlTimestamp = function (documentEl, tagSuffix, unixMs, options) {
  const opts = _.assign({ createIfMissing: false }, options || {})
  return upsertDocumentWpmlTimestamp(documentEl, tagSuffix, unixMs, {
    createIfMissing: !!opts.createIfMissing,
    namespaceUri: WPML_NAMESPACE_URI
  })
}
const fnEnsureKmzTimestampsOnMissionLoad = function (missionStateLike) {
  const mission = missionStateLike || importMissionState
  if (!mission) return false
  const docs = [mission.documentEl, mission.secondaryDocumentEl].filter(Boolean)
  if (!docs.length) return false
  const hasInvalid = docs.some((docEl) => {
    const createMs = readDocumentWpmlTimestampMs(docEl, 'createTime')
    const updateMs = readDocumentWpmlTimestampMs(docEl, 'updateTime')
    return !Number.isFinite(createMs) || createMs <= 0 || !Number.isFinite(updateMs) || updateMs <= 0
  })
  if (!hasInvalid) return false
  const nowMs = Date.now()
  return fnApplyKmzMissionTimestamps(mission, {
    createTimeMs: nowMs,
    updateTimeMs: nowMs,
    createIfMissing: true
  })
}
const fnApplyKmzMissionTimestamps = function (missionStateLike, options) {
  const mission = missionStateLike || importMissionState
  const opts = _.assign({
    createTimeMs: null,
    updateTimeMs: null,
    createIfMissing: false
  }, options || {})
  if (!mission) return false
  const docs = [mission.documentEl, mission.secondaryDocumentEl].filter(Boolean)
  if (!docs.length) return false
  let changed = false
  if (opts.createTimeMs !== null && opts.createTimeMs !== undefined) {
    docs.forEach((docEl) => {
      if (fnUpsertDocumentWpmlTimestamp(docEl, 'createTime', opts.createTimeMs, { createIfMissing: opts.createIfMissing })) {
        changed = true
      }
    })
  }
  if (opts.updateTimeMs !== null && opts.updateTimeMs !== undefined) {
    docs.forEach((docEl) => {
      if (fnUpsertDocumentWpmlTimestamp(docEl, 'updateTime', opts.updateTimeMs, { createIfMissing: opts.createIfMissing })) {
        changed = true
      }
    })
  }
  return changed
}
const fnScheduleKmzUpdateTimeTouch = function (changedPath) {
  if (kmzUpdateTimeTouchLock) return
  if (importMissionType !== 'kmz' || !importMissionState || !importMissionState.documentEl) return
  const pathLower = String(changedPath || '').toLowerCase()
  if (pathLower.includes('createtime') || pathLower.includes('updatetime')) return
  if (kmzUpdateTimeTouchTimer) return
  kmzUpdateTimeTouchTimer = setTimeout(function () {
    kmzUpdateTimeTouchTimer = null
    kmzUpdateTimeTouchLock = true
    try {
      const touched = fnApplyKmzMissionTimestamps(importMissionState, {
        updateTimeMs: Date.now(),
        createIfMissing: false
      })
      if (touched) {
        fnScheduleRoute3dSharedSync()
      }
    } finally {
      kmzUpdateTimeTouchLock = false
    }
  }, 750)
}

const fnEnableSwalDrag = function (popup) {
  if (!popup || popup.dataset.swalDragReady) return
  popup.dataset.swalDragReady = '1'
  const fnGetPortalNavbarBottom = function () {
    const navbar = document.querySelector('[data-modal-max-top]')
    if (!navbar || navbar.offsetParent === null) return 0
    const rect = navbar.getBoundingClientRect()
    if (!rect || !Number.isFinite(rect.bottom)) return 0
    return Math.max(0, Math.ceil(rect.bottom))
  }
  const titleEl = popup.querySelector('.swal2-title')
  if (titleEl) {
    if (!String(titleEl.textContent || '').trim()) {
      titleEl.textContent = 's rtesstses'
    }
    titleEl.style.cursor = 'move'
  }

  let isDragging = false
  let startX = 0
  let startY = 0
  let baseLeft = 0
  let baseTop = 0

  const onMouseMove = function (e) {
    if (!isDragging) return
    const dx = e.clientX - startX
    const dy = e.clientY - startY
    const rect = popup.getBoundingClientRect()
    const minTop = fnGetPortalNavbarBottom()
    const maxLeft = Math.max(0, window.innerWidth - rect.width)
    const maxTop = Math.max(minTop, window.innerHeight - rect.height)
    const nextLeft = Math.min(maxLeft, Math.max(0, baseLeft + dx))
    const nextTop = Math.min(maxTop, Math.max(minTop, baseTop + dy))
    popup.style.left = `${nextLeft}px`
    popup.style.top = `${nextTop}px`
  }

  const onMouseUp = function () {
    if (!isDragging) return
    isDragging = false
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  }

  popup.addEventListener('mousedown', function (e) {
    if (e.button !== 0) return
    const $t = $(e.target)
    if (!$t.closest('.swal2-title').length) return
    if ($t.closest('input, textarea, select, option, button, a, label, .swal2-actions, .swal2-footer, .swal2-close').length) {
      return
    }
    const rect = popup.getBoundingClientRect()
    popup.style.position = 'fixed'
    popup.style.margin = '0'
    popup.style.width = `${rect.width}px`
    popup.style.maxWidth = `${rect.width}px`
    popup.style.minWidth = `${rect.width}px`
    popup.style.left = `${rect.left}px`
    popup.style.top = `${Math.max(fnGetPortalNavbarBottom(), rect.top)}px`
    popup.style.transform = 'none'
    isDragging = true
    startX = e.clientX
    startY = e.clientY
    baseLeft = rect.left
    baseTop = rect.top
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  })
}

const fnInitSwalDragObserver = function () {
  if (window.__swalDragObserver) return
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (!(node instanceof HTMLElement)) return
        if (node.classList.contains('swal2-popup')) {
          fnEnableSwalDrag(node)
          return
        }
        const popup = node.querySelector && node.querySelector('.swal2-popup')
        if (popup) fnEnableSwalDrag(popup)
      })
    })
  })
  observer.observe(document.body, {childList: true, subtree: true})
  window.__swalDragObserver = observer
}

fnInitSwalDragObserver()

const areaSwal = baseSwal.mixin({
  icon: false,
  text: '',
  showClass: {popup: `animate__animated animate__fadeInUp animate__faster`},
  hideClass: {popup: `animate__animated animate__fadeOutDown animate__faster`}
})

const fnShowPropContextMenu = function (event, data) {
  showPortalPropContextMenu(event, data)
}

let map
let notamItems
let drawnItems
let importItems
let importLineItems
let tiffDemItems
let tiffOrthoItems
let tiffLayerBySource = { terrain: null, ortho: null }
let tiffLastFileBySource = { terrain: null, ortho: null }
let tiffParsedMemoryCacheBySource = {
  terrain: { signature: null, georaster: null },
  ortho: { signature: null, georaster: null }
}
const GEOTIFF_CACHE_INDEX_KEY = 'portal-geotiff-cache-index-v1'
const GEOTIFF_CACHE_KEY_PREFIX = 'portal-geotiff-cache-file-v1:'
const GEOTIFF_CACHE_FALLBACK_MAX_BYTES = 1024 * 1024 * 1024 // 1 GB
const GEOTIFF_CACHE_MIN_BYTES = 256 * 1024 * 1024 // 256 MB
const GEOTIFF_CACHE_HARD_MAX_BYTES = 2 * 1024 * 1024 * 1024 // 2 GB
const GEOTIFF_MAX_FILE_BYTES = 3 * 1024 * 1024 * 1024 // 3 GB
const KML_MAX_FILE_BYTES = 50 * 1024 * 1024 // 50 MB
const KMZ_MAX_FILE_BYTES = 50 * 1024 * 1024 // 50 MB
const GEOTIFF_ALLOWED_MIME_TYPES = ['image/tiff', 'image/geotiff', 'application/geotiff']
let geotiffCacheMaxBytes = GEOTIFF_CACHE_FALLBACK_MAX_BYTES
const GEOTIFF_DEBUG_LOG = false
let importMissionState = null
let importKmlFilename = null
let importMissionType = 'kml'
let importSelectedPoints = []
let importSelectMode = false
let importDragMode = false
let importAddPointMode = false
let importActivePoint = null
let importSelectDrawRect = null
let importSelectDrawPoly = null
let importSeekDrawLine = null
let importSeekMode = false
let importSeekAxisCallback = null
let importSeekDrawActive = false
let importSeekAxisUpBearingLast = null
let importHistory = { undo: [], redo: [] }
let importHistoryLock = false
const IMPORT_DRAFT_KEY = 'portal-import-draft-v1'
const IMPORT_HISTORY_MAX = 80
let importDraftPersistTimer = null
let importDivideNameRound = 0
let fnScheduleRoute3dSharedSync = function () {}

const myLocationController = createMyLocationController({
  $: $,
  $body: $body,
  L: L,
  baseSwal: baseSwal,
  fnT: fnT,
  fnGetMap: function () { return map },
  navigatorRef: navigator
})

const fnClearMyLocationLocateTimer = function () {
  return myLocationController.clearLocateTimer()
}

const fnSetMyLocationButtonActive = function (active) {
  return myLocationController.setButtonActive(active)
}

const fnApplyMyLocationLatLng = function (latlng) {
  return myLocationController.applyLatLng(latlng)
}

const fnTryBrowserMyLocationFallback = function () {
  return myLocationController.tryBrowserFallback()
}

const mapInitRefs = initializePortalMap({
  $: $,
  L: L,
  $leafletmap: $leafletmap,
  $body: $body,
  db: db,
  store: store,
  fnT: fnT,
  fnLayerFilterAll: fnLayerFilterAll,
  fnBindPolygonCreateClick: fnBindPolygonCreateClick,
  fnApplyMyLocationLatLng: fnApplyMyLocationLatLng,
  fnTryBrowserMyLocationFallback: fnTryBrowserMyLocationFallback,
  fnGetMyLocationActive: function () { return myLocationController.isActive() },
  fnSelectClear: function () { return fnSelectClear() },
  fnSelectToggle: function (point) { return fnSelectToggle(point) },
  fnPointInPolygon: function (point, polygon) { return fnPointInPolygon(point, polygon) },
  fnGetImportMissionState: function () { return importMissionState },
  fnGetImportSeekMode: function () { return importSeekMode },
  fnSetImportSeekMode: function (nextValue) { importSeekMode = !!nextValue },
  fnSetImportSeekDrawActive: function (nextValue) { importSeekDrawActive = !!nextValue },
  fnSetImportSeekDrawLine: function (nextValue) { importSeekDrawLine = nextValue || null },
  fnGetImportSeekAxisCallback: function () { return importSeekAxisCallback },
  fnSetImportSeekAxisCallback: function (nextValue) { importSeekAxisCallback = nextValue || null },
  fnGetImportSelectMode: function () { return importSelectMode },
  fnSetImportSelectMode: function (nextValue) { importSelectMode = !!nextValue },
  fnOnImportSelectModeResetUi: function () {
    $body.find('[data-leaf-select-square]').removeClass('active')
    $body.find('[data-leaf-select-poly]').removeClass('active')
  }
})
map = mapInitRefs.map
notamItems = mapInitRefs.notamItems
drawnItems = mapInitRefs.drawnItems
importItems = mapInitRefs.importItems
importLineItems = mapInitRefs.importLineItems
tiffDemItems = mapInitRefs.tiffDemItems
tiffOrthoItems = mapInitRefs.tiffOrthoItems

function fnBindPolygonCreateClick (layer) {
  return bindPolygonCreateClick({
    layer: layer,
    $: $,
    areaSwal: areaSwal,
    fnT: fnT,
    fnCoordToDmsc: function (value, coordType) { return nDDT_sDMSC(value, coordType) },
    fnGetAdminUrl: function () { return $body.data('adminUrl') },
    windowRef: window
  })
}

const layerCoreController = createLayerCoreController({
  $: $,
  _: _,
  L: L,
  momentRef: moment,
  windowRef: window,
  areaSwal: areaSwal,
  fnT: fnT,
  fnIsValidLayerCord: function (shape, cord) { return fnIsValidLayerCord(shape, cord) },
  fnGetMap: function () { return map },
  fnGetDb: function () { return db },
  fnGetNotamItems: function () { return notamItems }
})
const fnCordKey = function (cord) {
  return layerCoreController.cordKey(cord)
}
const fnCordIsOnMap = function (row, addToMap) {
  return layerCoreController.cordIsOnMap(row, addToMap)
}
function fnLayerFilterAll () {
  return layerCoreController.layerFilterAll()
}
const fnLayerBuilder = function (row, didOpen) {
  return layerCoreController.layerBuilder(row, didOpen)
}

const fnIsFiniteCoordPair = function (pair) {
  return Array.isArray(pair) &&
    pair.length === 2 &&
    pair.every((val) => Number.isFinite(Number(val)))
}

const fnIsValidLayerCord = function (shape, cord) {
  if (shape === 'circle') {
    return fnIsFiniteCoordPair(cord)
  }
  if (shape === 'polygon') {
    return Array.isArray(cord) &&
      cord.length >= 3 &&
      cord.every(fnIsFiniteCoordPair)
  }
  return false
}

const fnXmlToObj = function (el) {
  return portalPointMapUtils.xmlToObj(el)
}

const fnUpdateXmlByPath = function (xmlEl, path, value) {
  return portalPointMapUtils.updateXmlByPath(xmlEl, path, value, fnScheduleKmzUpdateTimeTouch)
}

const fnUpdateXmlCoordinates = function (xmlEl, lat, lng) {
  return portalPointMapUtils.updateXmlCoordinates(xmlEl, lat, lng, fnScheduleKmzUpdateTimeTouch)
}

let seekToolsController = null
const fnGetSeekToolsController = function () {
  if (!seekToolsController) {
    seekToolsController = createSeekTools({
      L: L,
      clamp,
      offsetLatLngByMeters,
      bearingBetweenLatLng,
      angleDiffDeg,
      axialDiffDeg,
      normalizeBearingDeg,
      roundToTenth,
      fnGetMap: function () { return map },
      fnGetMissionState: function () { return importMissionState },
      fnGetImportKmlFilename: function () { return importKmlFilename },
      fnGetDrawnItems: function () { return drawnItems },
      fnGetAxisUpBearingLast: function () { return importSeekAxisUpBearingLast },
      fnCreateInsertedPoint: fnCreateInsertedPoint,
      fnCaptureState: fnCaptureState,
      fnSyncMissionPlacemarkOrder: fnSyncMissionPlacemarkOrder,
      fnRenderImportLines: fnRenderImportLines,
      fnHistoryPush: fnHistoryPush,
      fnSelectClear: fnSelectClear
    })
  }
  return seekToolsController
}

const fnBuildSeekSegments = function (waypoints) {
  return fnGetSeekToolsController().buildSeekSegments(waypoints)
}

const fnFindSeekRouteBearing = function (segments) {
  return fnGetSeekToolsController().findSeekRouteBearing(segments)
}

const fnBuildSeekAxisLayerFromBearing = function (anchorLatLng, bearingDeg) {
  return fnGetSeekToolsController().buildSeekAxisLayerFromBearing(anchorLatLng, bearingDeg)
}

const fnResolveSeekAxisUpBearing = function (axisLayer) {
  return fnGetSeekToolsController().resolveSeekAxisUpBearing(axisLayer)
}

const fnBuildAutoSeekAxisLayer = function () {
  return fnGetSeekToolsController().buildAutoSeekAxisLayer()
}

const fnGetSeekAxisUpBearingForSuggest = function () {
  return fnGetSeekToolsController().getSeekAxisUpBearingForSuggest()
}

const fnBuildHeadingSuggestModel = function () {
  return fnGetSeekToolsController().buildHeadingSuggestModel()
}

const fnBuildSeekAxisLayerForPoint = function (latlng) {
  return fnGetSeekToolsController().buildSeekAxisLayerForPoint(latlng)
}

const fnCreateSeekAxisLayer = function (anchorLatLng, options) {
  return fnGetSeekToolsController().createSeekAxisLayer(anchorLatLng, options)
}

const fnAttachSeekAxisLayerToModal = function (modal, anchorLatLng, options) {
  return fnGetSeekToolsController().attachSeekAxisLayerToModal(modal, anchorLatLng, options)
}

const fnBuildSeekRowCandidates = function (axisUpBearing) {
  return fnGetSeekToolsController().buildSeekRowCandidates(axisUpBearing)
}

const fnGetImportWaypointsCenter = function () {
  return fnGetSeekToolsController().getImportWaypointsCenter()
}

const fnPickSeekRowCandidate = function (candidates, mode) {
  return fnGetSeekToolsController().pickSeekRowCandidate(candidates, mode)
}

const fnEstimateSeekCloneDistance = function (candidates, axisUpBearing) {
  return fnGetSeekToolsController().estimateSeekCloneDistance(candidates, axisUpBearing)
}

const fnSignedAxisOffsetMeters = function (fromLatLng, toLatLng, axisUpBearing) {
  return fnGetSeekToolsController().signedAxisOffsetMeters(fromLatLng, toLatLng, axisUpBearing)
}

const fnResolveCloneDirectionForTarget = function (candidate, axisUpBearing, targetMode, sourceMode) {
  return fnGetSeekToolsController().resolveCloneDirectionForTarget(candidate, axisUpBearing, targetMode, sourceMode)
}

const fnCloneSeekRow = function (candidate, axisUpBearing, targetMode, cloneDistanceMeters, sourceMode) {
  return fnGetSeekToolsController().cloneSeekRow(candidate, axisUpBearing, targetMode, cloneDistanceMeters, sourceMode)
}

const fnPointSetSelected = function (point, selected) {
  return portalPointMapUtils.pointSetSelected(point, selected, fnSetMarkerStyle)
}

const fnSelectClear = function () {
  importSelectedPoints = portalPointMapUtils.selectClear(importSelectedPoints, fnPointSetSelected)
}

const fnSelectToggle = function (point) {
  importSelectedPoints = portalPointMapUtils.selectToggle(importSelectedPoints, point, fnPointSetSelected)
}

const fnSelectAllPoints = function (points) {
  const allPoints = Array.isArray(points) ? points : ((importMissionState && importMissionState.waypoints) ? importMissionState.waypoints : [])
  fnSelectClear()
  importSelectedPoints = portalPointMapUtils.selectAllPoints(allPoints, fnPointSetSelected)
}

const fnBuildDividePointsPopupHtml = function (selectedCount, maxPairDistance) {
  return portalPointMapUtils.buildDividePointsPopupHtml(selectedCount, maxPairDistance)
}

const fnBuildMapFunctionsPopupHtml = function () {
  return portalPointMapUtils.buildMapFunctionsPopupHtml()
}

const fnBuildDemLoadingPopupHtml = function () {
  return portalPointMapUtils.buildDemLoadingPopupHtml()
}

const fnRegisterImportPoint = function (point, marker) {
  return portalPointMapUtils.registerImportPoint(point, marker)
}

const fnGetMarkerStyle = function (marker) {
  return portalPointMapUtils.getMarkerStyle(marker)
}

const fnPointInPolygon = function (point, polygon) {
  return portalPointMapUtils.pointInPolygon(point, polygon)
}

const fnCaptureState = function () {
  return portalImportStateUtils.captureState(importMissionType, importMissionState)
}

const fnNormalizeStateSnapshot = function (snapshot) {
  return portalImportStateUtils.normalizeStateSnapshot(snapshot)
}

const fnNormalizeHistoryStack = function (stack) {
  return portalImportStateUtils.normalizeHistoryStack(stack, IMPORT_HISTORY_MAX, fnNormalizeStateSnapshot)
}

const fnPersistImportDraftNow = async function () {
  return portalImportStateUtils.persistImportDraftNow({
    windowRef: window,
    importDraftKey: IMPORT_DRAFT_KEY,
    importKmlFilename: importKmlFilename,
    importHistory: importHistory,
    fnCaptureState: fnCaptureState,
    fnNormalizeStateSnapshot: fnNormalizeStateSnapshot,
    fnNormalizeHistoryStack: fnNormalizeHistoryStack,
    fnT: fnT
  })
}

const fnSchedulePersistImportDraft = function () {
  if (importDraftPersistTimer) {
    clearTimeout(importDraftPersistTimer)
  }
  importDraftPersistTimer = setTimeout(function () {
    importDraftPersistTimer = null
    void fnPersistImportDraftNow()
  }, 150)
}

const fnClearPersistedImportDraft = async function () {
  return portalImportStateUtils.clearPersistedImportDraft({
    windowRef: window,
    importDraftKey: IMPORT_DRAFT_KEY,
    fnGetImportDraftPersistTimer: function () { return importDraftPersistTimer },
    fnSetImportDraftPersistTimer: function (value) { importDraftPersistTimer = value },
    fnT: fnT
  })
}

const fnRestoreState = function (state) {
  return portalImportStateUtils.restoreState(state, {
    importItems: importItems,
    importLineItems: importLineItems,
    fnSetImportHistoryLock: function (value) { importHistoryLock = !!value },
    fnSetImportMissionState: function (value) { importMissionState = value },
    fnSetImportSelectedPoints: function (value) { importSelectedPoints = Array.isArray(value) ? value : [] },
    fnParseKmzMission: fnParseKmzMission,
    fnParseKmlMission: fnParseKmlMission,
    fnSetImportMissionType: function (value) { importMissionType = (value === 'kmz') ? 'kmz' : 'kml' },
    fnSetTemplateWaypointDefaultsCache: function (value) { templateWaypointDefaultsCache = value },
    fnGetImportKmlFilename: function () { return importKmlFilename },
    fnSetImportKmlFilename: function (value) { importKmlFilename = value },
    fnRenderImportLines: fnRenderImportLines,
    fnGetImportMissionState: function () { return importMissionState },
    fnGetMissionAltitudeContextFor3d: fnGetMissionAltitudeContextFor3d,
    fnRecalculateEgm96EllipsoidForPoints: fnRecalculateEgm96EllipsoidForPoints,
    fnScheduleRoute3dSharedSync: fnScheduleRoute3dSharedSync
  })
}

const fnHistoryPush = function (snapshot) {
  return portalImportStateUtils.historyPush(snapshot, {
    importHistory: importHistory,
    importHistoryMax: IMPORT_HISTORY_MAX,
    fnGetImportHistoryLock: function () { return importHistoryLock === true },
    fnCaptureState: fnCaptureState,
    fnNormalizeStateSnapshot: fnNormalizeStateSnapshot,
    fnSchedulePersistImportDraft: fnSchedulePersistImportDraft
  })
}

const fnHistoryUndo = function () {
  return portalImportStateUtils.historyUndo({
    importHistory: importHistory,
    importHistoryMax: IMPORT_HISTORY_MAX,
    fnCaptureState: fnCaptureState,
    fnNormalizeStateSnapshot: fnNormalizeStateSnapshot,
    fnRestoreState: fnRestoreState,
    fnSchedulePersistImportDraft: fnSchedulePersistImportDraft
  })
}

const fnHistoryRedo = function () {
  return portalImportStateUtils.historyRedo({
    importHistory: importHistory,
    importHistoryMax: IMPORT_HISTORY_MAX,
    fnCaptureState: fnCaptureState,
    fnNormalizeStateSnapshot: fnNormalizeStateSnapshot,
    fnRestoreState: fnRestoreState,
    fnSchedulePersistImportDraft: fnSchedulePersistImportDraft
  })
}

const fnSetMarkerStyle = function (marker, style) {
  return portalPointMapUtils.setMarkerStyle(marker, style)
}

const fnGetPointXmlNodes = function (point) {
  return portalPointMapUtils.getPointXmlNodes(point)
}

const fnUpdatePointXmlByPath = function (point, path, value) {
  return portalPointMapUtils.updatePointXmlByPath(point, path, value, fnUpdateXmlByPath)
}

const fnUpdatePointCoordinates = function (point, lat, lng) {
  return portalPointMapUtils.updatePointCoordinates(point, lat, lng, fnUpdateXmlCoordinates)
}

const fnRemovePointXmlNodes = function (point) {
  return portalPointMapUtils.removePointXmlNodes(point)
}

const fnSyncPlacemarkOrder = function (parentEl, points) {
  return portalPointMapUtils.syncPlacemarkOrder(parentEl, points)
}

const fnResolveInsertPlacementForLatLng = function (waypoints, latlng) {
  return portalPointMapUtils.resolveInsertPlacementForLatLng(waypoints, latlng, map)
}

const fnFindInsertIndexForLatLng = function (waypoints, latlng) {
  return portalPointMapUtils.findInsertIndexForLatLng(waypoints, latlng, map)
}

const fnCreateInsertedPoint = function (basePoint, latlng, altitudeOverride) {
  return portalPointMapUtils.createInsertedPoint(basePoint, latlng, altitudeOverride, {
    importMissionState: importMissionState,
    fnGetMissionAltitudeContextFor3d: fnGetMissionAltitudeContextFor3d,
    fnApplyPointHeightValue: fnApplyPointHeightValue,
    fnTemplateSupportsPointCoordinatesField: fnTemplateSupportsPointCoordinatesField,
    fnBuildPointCoordinatesText: fnBuildPointCoordinatesText,
    fnSetByPath: fnSetByPath,
    fnUpdatePointCoordinates: fnUpdatePointCoordinates,
    fnUpdatePointXmlByPath: fnUpdatePointXmlByPath
  })
}

const fnGetPointName = function (point) {
  return portalImportStateUtils.getPointName(point)
}

const fnSetPointKmlActionsWithContext = function (point, actions) {
  return fnSetPointKmlActions(point, actions, fnGetPointXmlNodes)
}

const fnSetPointKmzActionGroupsWithContext = function (point, groups, options) {
  return fnSetPointKmzActionGroups(
    point,
    groups,
    options,
    fnGetPointXmlNodes,
    fnScheduleKmzUpdateTimeTouch,
    WPML_NAMESPACE_URI
  )
}

const fnSyncPointActionFileSuffixWithNameWithContext = function (point, explicitName) {
  return fnSyncPointActionFileSuffixWithName(
    point,
    explicitName,
    fnGetPointName,
    fnSetPointKmzActionGroupsWithContext
  )
}

const fnSetPointName = function (point, name) {
  return portalImportStateUtils.setPointName(point, name, fnSyncPointActionFileSuffixWithNameWithContext)
}

const fnRenameWaypointsLinear = function (waypoints, options) {
  return portalImportStateUtils.renameWaypointsLinear(
    waypoints,
    options,
    fnSetPointName,
    fnSetPointDescription,
    fnGetWaypointTooltipText
  )
}

const fnGetPointDescription = function (point) {
  return portalImportStateUtils.getPointDescription(point)
}

const fnSetPointDescription = function (point, description) {
  return portalImportStateUtils.setPointDescription(
    point,
    description,
    fnGetPointXmlNodes,
    fnUpdateXmlByPath,
    importMissionType,
    document
  )
}

const fnBuildDemLine = function (providerKey, elevationMeters) {
  return portalAltitudeUtils.buildDemLine(providerKey, elevationMeters)
}

const fnFormatMeters = function (value) {
  return portalAltitudeUtils.formatMeters(value)
}

const fnBuildAglLine = function (providerKey, demMeters, altitudeMeters, aglMeters) {
  return portalAltitudeUtils.buildAglLine(providerKey, demMeters, altitudeMeters, aglMeters)
}

const fnGetPointDemValues = function (point) {
  return portalAltitudeUtils.getPointDemValues(point)
}

const fnGetPreferredPointDemValue = function (point) {
  return portalAltitudeUtils.getPreferredPointDemValue(point, fnBuildAltitudeContext())
}

const fnGetFirstNumericFromPointXmlTags = function (point, tagNames) {
  return portalAltitudeUtils.getFirstNumericFromPointXmlTags(point, tagNames, fnBuildAltitudeContext())
}

const fnGetCoordinateAltFromPointXml = function (point) {
  return portalAltitudeUtils.getCoordinateAltFromPointXml(point, fnBuildAltitudeContext())
}

const POINT_HEIGHT_PATH_ALIASES = [
  'Point.height',
  'wpml:height',
  'height',
  'template.wpml:height',
  'waylines.wpml:height'
]

const POINT_EXECUTE_HEIGHT_PATH_ALIASES = [
  'Point.executeHeight',
  'wpml:executeHeight',
  'executeHeight',
  'template.wpml:executeHeight',
  'waylines.wpml:executeHeight'
]

const POINT_ELLIPSOID_HEIGHT_PATH_ALIASES = [
  'wpml:ellipsoidHeight',
  'ellipsoidHeight',
  'template.wpml:ellipsoidHeight',
  'waylines.wpml:ellipsoidHeight'
]

const fnBuildAltitudeContext = function () {
  return {
    importMissionState: importMissionState,
    DEM_PROVIDER_PRIORITY: DEM_PROVIDER_PRIORITY,
    ALTITUDE_RULES: ALTITUDE_RULES,
    POINT_HEIGHT_PATH_ALIASES: POINT_HEIGHT_PATH_ALIASES,
    POINT_EXECUTE_HEIGHT_PATH_ALIASES: POINT_EXECUTE_HEIGHT_PATH_ALIASES,
    POINT_ELLIPSOID_HEIGHT_PATH_ALIASES: POINT_ELLIPSOID_HEIGHT_PATH_ALIASES,
    NON_EXPORT_POINT_HEIGHT_PATH: NON_EXPORT_POINT_HEIGHT_PATH,
    fnIsDemProviderEnabled: fnIsDemProviderEnabled,
    fnGetPointXmlNodes: fnGetPointXmlNodes,
    fnSetByPath: fnSetByPath,
    fnUpdatePointXmlByPath: fnUpdatePointXmlByPath,
    fnXmlToObj: fnXmlToObj,
    fnTemplateSupportsPointCoordinatesField: fnTemplateSupportsPointCoordinatesField,
    fnBuildPointCoordinatesText: fnBuildPointCoordinatesText,
    fnGetPointName: fnGetPointName,
    fnGetPointDescription: fnGetPointDescription
  }
}

const fnGetPointNumericByPathAliases = function (point, aliases) {
  return portalAltitudeUtils.getPointNumericByPathAliases(point, aliases)
}

const fnSetPointValueByPathAliases = function (point, aliases, value) {
  return portalAltitudeUtils.setPointValueByPathAliases(point, aliases, value, fnBuildAltitudeContext())
}

const fnComputeDefaultEgm96OffsetForPoints = function (points) {
  return portalAltitudeUtils.computeDefaultEgm96OffsetForPoints(points, fnBuildAltitudeContext())
}

const fnGetPointCachedEgm96Offset = function (point) {
  return portalAltitudeUtils.getPointCachedEgm96Offset(point)
}

const fnSetPointCachedEgm96Offset = function (point, offset) {
  return portalAltitudeUtils.setPointCachedEgm96Offset(point, offset)
}

const fnSyncPointCachedEgm96Offset = function (point) {
  return portalAltitudeUtils.syncPointCachedEgm96Offset(point, fnBuildAltitudeContext())
}

const fnRecalculateEgm96EllipsoidForPoints = function (points, missionAltContext) {
  return portalAltitudeUtils.recalculateEgm96EllipsoidForPoints(points, missionAltContext, fnBuildAltitudeContext())
}

const fnIsEllipsoidHeightPath = function (path) {
  return portalAltitudeUtils.isEllipsoidHeightPath(path)
}

const fnIsWaypointHeightPath = function (path) {
  return portalAltitudeUtils.isWaypointHeightPath(path, fnBuildAltitudeContext())
}

const fnNormalizeAltitudeMode = function (modeRaw) {
  return portalAltitudeUtils.normalizeAltitudeMode(modeRaw, fnBuildAltitudeContext())
}

const fnResolveAltitudeReferenceType = function (mode, scope) {
  return portalAltitudeUtils.resolveAltitudeReferenceType(mode, scope, fnBuildAltitudeContext())
}

const fnGetMissionAltitudeContextFor3d = function (missionState) {
  return portalAltitudeUtils.getMissionAltitudeContextFor3d(missionState, fnBuildAltitudeContext())
}

const fnGetPointAltitudeFor3d = function (point, demMeters, missionAltContext, options) {
  return portalAltitudeUtils.getPointAltitudeFor3d(point, demMeters, missionAltContext, options, fnBuildAltitudeContext())
}

const fnSetPointDemValue = function (point, providerKey, elevationMeters) {
  return portalAltitudeUtils.setPointDemValue(point, providerKey, elevationMeters)
}

const fnGetPointAglValues = function (point) {
  return portalAltitudeUtils.getPointAglValues(point)
}

const fnSetPointAglValue = function (point, providerKey, altitudeMeters, aglMeters) {
  return portalAltitudeUtils.setPointAglValue(point, providerKey, altitudeMeters, aglMeters)
}

const fnGetPointHeightValue = function (point) {
  return portalAltitudeUtils.getPointHeightValue(point, fnBuildAltitudeContext())
}

const fnGetPointRawHeightValue = function (point) {
  return portalAltitudeUtils.getPointRawHeightValue(point, fnBuildAltitudeContext())
}

const fnApplyPointHeightValue = function (point, heightValue, missionAltContext) {
  return portalAltitudeUtils.applyPointHeightValue(point, heightValue, missionAltContext, fnBuildAltitudeContext())
}

const fnGetWaypointTooltipText = function (point) {
  return portalAltitudeUtils.getWaypointTooltipText(point, fnBuildAltitudeContext())
}

const fnRoundLetter = function (idx) {
  return portalImportStateUtils.roundLetter(idx)
}

const fnPadNumber = function (value, width) {
  return portalImportStateUtils.padNumber(value, width)
}

const fnApplyDivideRowNames = function (renameRows) {
  return portalImportStateUtils.applyDivideRowNames(
    renameRows,
    fnSetPointName,
    fnSetPointDescription,
    fnGetWaypointTooltipText,
    fnPadNumber
  )
}

const fnBuildDivideRenameRowsFromCreatedPoints = function (createdRows) {
  return portalImportStateUtils.buildDivideRenameRowsFromCreatedPoints(createdRows)
}

const fnParseKmlMission = function (xmlText) {
  return parseKmlMission(xmlText, fnXmlToObj)
}

const fnGetPointHeading = function (point) {
  return portalWaypointCoreUtils.getPointHeading(point)
}

const fnBuildWaypointIcon = function (point, fillColor) {
  return portalWaypointCoreUtils.buildWaypointIcon(point, fillColor, importMissionState)
}

const fnRefreshWaypointMarker = function (point) {
  return portalWaypointCoreUtils.refreshWaypointMarker(point, importMissionState, fnGetMarkerStyle, fnSetMarkerStyle)
}

const fnSetByPath = function (obj, path, value) {
  return portalWaypointCoreUtils.setByPath(obj, path, value)
}

const fnPropsToMap = function (props) {
  return portalWaypointCoreUtils.propsToMap(props)
}

let templateWaypointDefaultsCache = null

const fnGetTemplateWaypointDefaults = function () {
  if (templateWaypointDefaultsCache !== null) {
    return _.cloneDeep(templateWaypointDefaultsCache)
  }
  const templateId = (importMissionType === 'kmz') ? '[data-tpl-new-kmz-template]' : '[data-tpl-new-kml-pattern]'
  const templateXml = String($(templateId).text() || '').trim()
  if (!templateXml) {
    templateWaypointDefaultsCache = {}
    return {}
  }
  try {
    const parsedMission = (importMissionType === 'kmz')
      ? fnParseKmzMission(templateXml, String($('[data-tpl-new-kmz-waylines]').text() || '').trim())
      : fnParseKmlMission(templateXml)
    const firstPoint = (parsedMission && Array.isArray(parsedMission.waypoints)) ? parsedMission.waypoints[0] : null
    templateWaypointDefaultsCache = firstPoint ? fnPropsToMap(firstPoint.props || {}) : {}
  } catch (error) {
    console.warn(fnT('log.template_waypoint_parse_error'), error)
    templateWaypointDefaultsCache = {}
  }
  return _.cloneDeep(templateWaypointDefaultsCache)
}

const fnHasXmlAncestorTag = function (xmlEl, tagName) {
  return portalWaypointCoreUtils.hasXmlAncestorTag(xmlEl, tagName)
}

const fnBuildProjectDocumentClone = function (documentEl) {
  return portalWaypointCoreUtils.buildProjectDocumentClone(documentEl)
}

const fnOpenProjectSettingsEditor = function () {
  return openProjectSettingsEditor({
    importMissionState: importMissionState,
    importMissionType: importMissionType,
    importKmlFilename: importKmlFilename
  }, {
    _: _,
    $: $,
    Swal: Swal,
    areaSwal: areaSwal,
    fnBuildProjectDocumentClone: fnBuildProjectDocumentClone,
    fnPropsToMap: fnPropsToMap,
    fnXmlToObj: fnXmlToObj,
    fnT: fnT,
    fnTF: fnTF,
    fnCaptureState: fnCaptureState,
    fnHistoryPush: fnHistoryPush,
    fnRenderPropEditorRows: function ($container, rows, options) {
      return renderPortalPropEditorRows(
        $container,
        rows,
        options,
        buildRenderPropEditorRowsDeps({
          _,
          $,
          fnT,
          NON_EXPORT_PROJECT_FILENAME_PATH,
          NON_EXPORT_POINT_HEIGHT_PATH,
          DEM_PROVIDER_PRIORITY,
          fnGetDemProviderLabel,
          fnRunPointHeightDemOverwriteFlow,
          fnGetPointHeightToolFieldMeta,
          fnGetPointNumericByPathAliases,
          toFiniteNumber,
          pathWithoutNamespacePrefixes,
          pathWithoutNumericParts,
          fnResolvePropPresetOptions,
          getPathLeafField,
          pathWithoutLeadingScope,
          fnResolvePropLabelTooltip,
          fnResolvePropLabelDetailedHelp,
          fnResolvePropLabelIconClass,
          fnResolvePropLabelRequired,
          fnBuildHeadingSuggestModel,
          toUnixMs,
          fnFormatUnixMsForDisplay,
          fnParseDisplayTimestampToUnixMs,
          TIMESTAMP_DISPLAY_FORMAT,
          fnResolveI18Text,
          fnResolvePropValueHelp,
          fnShowPropContextMenu,
          PROP_FIELD_DISABLE_RULES
        })
      )
    },
    fnUpdateXmlByPath: fnUpdateXmlByPath,
    fnGetMissionAltitudeContextFor3d: fnGetMissionAltitudeContextFor3d,
    fnNormalizeAltitudeMode: fnNormalizeAltitudeMode,
    fnRecalculateEgm96EllipsoidForPoints: fnRecalculateEgm96EllipsoidForPoints,
    fnScheduleRoute3dSharedSync: fnScheduleRoute3dSharedSync,
    fnSetImportKmlFilename: function (nextFileName) {
      importKmlFilename = nextFileName
    }
  })
}

const fnMergePropsForEdit = function (points) {
  return portalWaypointCoreUtils.mergePropsForEdit(
    points,
    importMissionType,
    NON_EXPORT_POINT_HEIGHT_PATH,
    fnGetTemplateWaypointDefaults
  )
}

const fnTemplateSupportsPointCoordinatesField = function () {
  return portalWaypointCoreUtils.templateSupportsPointCoordinatesField(fnGetTemplateWaypointDefaults)
}

const fnFilterPointEditorMapForMissionType = function (map) {
  return portalWaypointCoreUtils.filterPointEditorMapForMissionType(
    map,
    importMissionType,
    fnGetTemplateWaypointDefaults,
    fnTemplateSupportsPointCoordinatesField
  )
}

const fnApplyEditsToPoints = function (points, edits) {
  const missionAltContext = fnGetMissionAltitudeContextFor3d(importMissionState)
  const shouldPersistPointCoordinates = fnTemplateSupportsPointCoordinatesField()
  const editedPaths = Object.keys(edits || {})
  const hasWaypointHeightEdit = editedPaths.some((path) => fnIsWaypointHeightPath(path))
  const hasEllipsoidHeightEdit = editedPaths.some((path) => fnIsEllipsoidHeightPath(path))
  points.forEach((pt) => {
    let hasCoordinateEdit = false
    let hasAltitudeEdit = false
    editedPaths.forEach((path) => {
      if (path === 'wpml:index' || path === 'index') return
      const val = edits[path]
      fnSetByPath(pt.props, path, val)
      if (String(path || '').toLowerCase() !== NON_EXPORT_POINT_HEIGHT_PATH) {
        fnUpdatePointXmlByPath(pt, path, val)
      }
      if (String(path || '').toLowerCase() === 'name') {
        fnSyncPointActionFileSuffixWithNameWithContext(pt, val)
      }
      if (path === 'coordinates.lat' || path === 'coordinates.lng' || path === 'coordinates.alt') {
        hasCoordinateEdit = true
      }
      const pathLower = String(path || '').toLowerCase()
      if (pathLower !== NON_EXPORT_POINT_HEIGHT_PATH && (pathLower.includes('coordinates.alt') || pathLower.includes('executeheight') || pathLower.includes('ellipsoidheight') || pathLower.endsWith('.height') || pathLower === 'height' || pathLower.includes('wpml:height'))) {
        hasAltitudeEdit = true
      }
      if (path === 'coordinates.lat' || path === 'coordinates.lng') {
        const lat = parseFloat(pt.props.coordinates && pt.props.coordinates.lat)
        const lng = parseFloat(pt.props.coordinates && pt.props.coordinates.lng)
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          pt.latlng = [lat, lng]
          if (pt.marker) pt.marker.setLatLng(pt.latlng)
          fnUpdatePointCoordinates(pt, lat, lng)
          if (pt.props && pt.props.coordinates) {
            pt.props.coordinates.lat = lat
            pt.props.coordinates.lng = lng
          }
        }
      }
    })
    if (hasCoordinateEdit && shouldPersistPointCoordinates) {
      const pointCoordinatesText = fnBuildPointCoordinatesText(pt.props)
      fnSetByPath(pt.props, 'Point.coordinates', pointCoordinatesText)
      fnUpdatePointXmlByPath(pt, 'Point.coordinates', pointCoordinatesText)
    }
    if (hasWaypointHeightEdit) {
      const egmChanged = fnRecalculateEgm96EllipsoidForPoints([pt], missionAltContext)
      if (egmChanged > 0) hasAltitudeEdit = true
    }
    if (hasEllipsoidHeightEdit) {
      fnSyncPointCachedEgm96Offset(pt)
    }
    if (hasAltitudeEdit || hasCoordinateEdit) {
      const resolvedAltitude = fnGetPointAltitudeFor3d(pt, fnGetPreferredPointDemValue(pt), missionAltContext)
      if (resolvedAltitude !== null) {
        pt.altitude = resolvedAltitude
      }
    }
    if (pt.marker && typeof pt.marker.setTooltipContent === 'function') {
      pt.marker.setTooltipContent(fnGetWaypointTooltipText(pt))
    }
    fnRefreshWaypointMarker(pt)
  })
  fnRefreshImportLine()
  fnScheduleRoute3dSharedSync()
}

const fnUpdateMissionLineCoords = function () {
  return portalWaypointCoreUtils.updateMissionLineCoords(importMissionState)
}

const fnRefreshImportLine = function () {
  return portalWaypointCoreUtils.refreshImportLine(
    importMissionState,
    fnUpdateMissionLineCoords,
    fnScheduleRoute3dSharedSync
  )
}

const fnSyncAddPointCursor = function () {
  return portalWaypointCoreUtils.syncAddPointCursor(importMissionState, importAddPointMode)
}

const fnSetWaypointDragging = function (enabled) {
  return portalWaypointCoreUtils.setWaypointDragging(importMissionState, enabled)
}

const fnBuildPointCoordinatesText = function (props) {
  return portalWaypointCoreUtils.buildPointCoordinatesText(props, importMissionType)
}

const fnNormalizeKmzWaypointForExport = function (point, index, options) {
  const opts = _.assign({
    actionGroupIdAllocator: null
  }, options || {})
  return normalizeKmzWaypointForExport(
    point,
    index,
    opts.actionGroupIdAllocator,
    fnSetByPath,
    fnUpdatePointXmlByPath,
    fnGetKmzActionGroupsFromProps,
    fnSetPointKmzActionGroupsWithContext,
    fnToBoolLike
  )
}

const fnNormalizeKmzMissionLevelForExport = function () {
  return normalizeKmzMissionLevelForExport(importMissionState)
}

const fnNormalizeKmzMissionForExport = function () {
  if (importMissionType !== 'kmz') return
  if (!importMissionState || !Array.isArray(importMissionState.waypoints)) return
  fnNormalizeKmzMissionLevelForExport()
  let actionGroupIdCounter = 0
  const fnNextActionGroupId = function () {
    const next = actionGroupIdCounter
    actionGroupIdCounter += 1
    return next
  }
  importMissionState.waypoints.forEach((pt, index) => {
    fnNormalizeKmzWaypointForExport(pt, index, {
      actionGroupIdAllocator: fnNextActionGroupId
    })
  })
}

const fnBuildKmzCanonicalExportDocs = function () {
  const templateXml = String($('[data-tpl-new-kmz-template]').text() || '').trim()
  const waylinesXml = String($('[data-tpl-new-kmz-waylines]').text() || '').trim()
  return buildKmzCanonicalExportDocs(
    importMissionState,
    templateXml,
    waylinesXml,
    fnUpdateXmlByPath
  )
}

const fnParseKmzMission = function (templateXmlText, waylinesXmlText) {
  return parseKmzMission(templateXmlText, waylinesXmlText, fnXmlToObj)
}

const pointEditLaunchers = createPortalPointEditLaunchers(function () {
  return buildPointEditLauncherOptions({
    _,
    $,
    areaSwal,
    drawnItems,
    fnT,
    fnTF,
    importMissionType,
    importMissionState,
    NON_EXPORT_PROJECT_FILENAME_PATH,
    NON_EXPORT_POINT_HEIGHT_PATH,
    DEM_PROVIDER_PRIORITY,
    fnGetDemProviderLabel,
    fnRunPointHeightDemOverwriteFlow,
    fnGetPointHeightToolFieldMeta,
    POINT_ELLIPSOID_HEIGHT_PATH_ALIASES,
    fnCaptureState,
    fnHistoryPush,
    fnAttachSeekAxisLayerToModal,
    fnGetTemplateWaypointDefaults,
    fnTemplateSupportsPointCoordinatesField,
    fnPropsToMap,
    fnSortPointEditorRows: function (rows) {
      return portalWaypointCoreUtils.sortPointEditorRows(rows, NON_EXPORT_POINT_HEIGHT_PATH)
    },
    fnPropsMapToRows: function (map) {
      return portalWaypointCoreUtils.propsMapToRows(map, PROP_SORT_PREFIX_PRIORITY)
    },
    fnFilterPointEditorMapForMissionType,
    fnIsKmlActionPath,
    fnIsKmzActionPath,
    fnSetByPath,
    fnUpdatePointXmlByPath,
    fnSyncPointActionFileSuffixWithNameWithContext,
    fnIsWaypointHeightPath,
    fnGetMissionAltitudeContextFor3d,
    fnRecalculateEgm96EllipsoidForPoints,
    fnGetPointNumericByPathAliases,
    fnIsEllipsoidHeightPath,
    fnBuildPointCoordinatesText,
    fnUpdatePointCoordinates,
    fnSyncRoute3dSharedState,
    fnScheduleRoute3dSharedSync,
    fnRefreshWaypointMarker,
    fnGetKmlActionsFromProps,
    fnSetKmlActionsInProps,
    fnSetPointKmlActionsWithContext,
    fnApplyKmzActionFileSuffixDisplayModeToGroups,
    fnGetKmzActionGroupsFromProps,
    fnGetPointName,
    fnResolveKmzActionFileSuffixPointNameTokensInGroups,
    fnSetKmzActionGroupsInProps,
    fnSetPointKmzActionGroupsWithContext,
    fnMergePropsForEdit,
    fnApplyEditsToPoints,
    fnBuildSharedKmlActionsForPoints,
    fnBuildSharedKmzActionGroupsForPoints,
    toFiniteNumber,
    pathWithoutNamespacePrefixes,
    pathWithoutNumericParts,
    fnResolvePropPresetOptions,
    getPathLeafField,
    pathWithoutLeadingScope,
    fnResolvePropLabelTooltip,
    fnResolvePropLabelDetailedHelp,
    fnResolvePropLabelIconClass,
    fnResolvePropLabelRequired,
    fnBuildHeadingSuggestModel,
    toUnixMs,
    fnFormatUnixMsForDisplay,
    fnParseDisplayTimestampToUnixMs,
    TIMESTAMP_DISPLAY_FORMAT,
    fnResolveI18Text,
    fnResolvePropValueHelp,
    fnShowPropContextMenu,
    PROP_FIELD_DISABLE_RULES
  })
})

const fnSwalProps = function (title, point, onCoordsChange, onPropsChange) {
  return pointEditLaunchers.openPortalPointPropsSwal(title, point, onCoordsChange, onPropsChange)
}

const fnSwalBulkEdit = function (points) {
  return pointEditLaunchers.openPortalBulkEditSwal(points)
}

const fnBuildMissionRenderContext = function () {
  return {
    _: _,
    $: $,
    L: L,
    windowRef: window,
    momentRef: moment,
    Swal: Swal,
    map: map,
    $body: $body,
    importItems: importItems,
    importLineItems: importLineItems,
    IMPORT_DRAFT_KEY: IMPORT_DRAFT_KEY,
    POINT_HEIGHT_PATH_ALIASES: POINT_HEIGHT_PATH_ALIASES,
    POINT_EXECUTE_HEIGHT_PATH_ALIASES: POINT_EXECUTE_HEIGHT_PATH_ALIASES,
    NON_EXPORT_POINT_HEIGHT_PATH: NON_EXPORT_POINT_HEIGHT_PATH,
    state: {
      get importMissionState () { return importMissionState },
      set importMissionState (nextValue) { importMissionState = nextValue },
      get importMissionType () { return importMissionType },
      set importMissionType (nextValue) { importMissionType = String(nextValue || 'kml') === 'kmz' ? 'kmz' : 'kml' },
      get importKmlFilename () { return importKmlFilename },
      set importKmlFilename (nextValue) { importKmlFilename = nextValue || null },
      get templateWaypointDefaultsCache () { return templateWaypointDefaultsCache },
      set templateWaypointDefaultsCache (nextValue) { templateWaypointDefaultsCache = nextValue || null },
      get importAddPointMode () { return importAddPointMode },
      set importAddPointMode (nextValue) { importAddPointMode = !!nextValue },
      get importDragMode () { return importDragMode },
      set importDragMode (nextValue) { importDragMode = !!nextValue },
      get importActivePoint () { return importActivePoint },
      set importActivePoint (nextValue) { importActivePoint = nextValue || null },
      get importSelectMode () { return importSelectMode },
      set importSelectMode (nextValue) { importSelectMode = !!nextValue },
      get importHistoryLock () { return importHistoryLock },
      set importHistoryLock (nextValue) { importHistoryLock = !!nextValue },
      get importSeekMode () { return importSeekMode },
      set importSeekMode (nextValue) { importSeekMode = !!nextValue },
      get importSeekDrawActive () { return importSeekDrawActive },
      set importSeekDrawActive (nextValue) { importSeekDrawActive = !!nextValue },
      get importSeekDrawLine () { return importSeekDrawLine },
      set importSeekDrawLine (nextValue) { importSeekDrawLine = nextValue || null },
      get importSeekAxisCallback () { return importSeekAxisCallback },
      set importSeekAxisCallback (nextValue) { importSeekAxisCallback = nextValue || null },
      get importSelectDrawRect () { return importSelectDrawRect },
      set importSelectDrawRect (nextValue) { importSelectDrawRect = nextValue || null },
      get importSelectDrawPoly () { return importSelectDrawPoly },
      set importSelectDrawPoly (nextValue) { importSelectDrawPoly = nextValue || null },
      get importHistory () { return importHistory },
      set importHistory (nextValue) { importHistory = nextValue || { undo: [], redo: [] } }
    },
    fnSyncMissionPlacemarkOrder: fnSyncMissionPlacemarkOrder,
    fnBindPolygonCreateClick: fnBindPolygonCreateClick,
    fnT: fnT,
    fnStyle: function (type, row) { return fnStyle(type, row) },
    fnSyncAddPointCursor: fnSyncAddPointCursor,
    fnCaptureState: fnCaptureState,
    fnResolveInsertPlacementForLatLng: fnResolveInsertPlacementForLatLng,
    fnGetPointNumericByPathAliases: fnGetPointNumericByPathAliases,
    toFiniteNumber,
    interpolateByRatioNullable,
    fnCreateInsertedPoint: fnCreateInsertedPoint,
    fnSetByPath: fnSetByPath,
    roundToTenth,
    fnSetPointValueByPathAliases: fnSetPointValueByPathAliases,
    fnRenameWaypointsLinear: fnRenameWaypointsLinear,
    fnHistoryPush: fnHistoryPush,
    fnSelectClear: fnSelectClear,
    fnBuildWaypointIcon: fnBuildWaypointIcon,
    fnGetWaypointTooltipText: fnGetWaypointTooltipText,
    fnRegisterImportPoint: fnRegisterImportPoint,
    fnSelectToggle: fnSelectToggle,
    fnSwalProps: function () { return fnSwalProps.apply(null, arguments) },
    fnUpdatePointCoordinates: fnUpdatePointCoordinates,
    fnRefreshImportLine: fnRefreshImportLine,
    fnGetMissionAltitudeContextFor3d: fnGetMissionAltitudeContextFor3d,
    fnIsWaypointHeightPath: fnIsWaypointHeightPath,
    fnRecalculateEgm96EllipsoidForPoints: fnRecalculateEgm96EllipsoidForPoints,
    fnIsEllipsoidHeightPath: fnIsEllipsoidHeightPath,
    fnSyncPointCachedEgm96Offset: fnSyncPointCachedEgm96Offset,
    fnGetPointAltitudeFor3d: fnGetPointAltitudeFor3d,
    fnGetPreferredPointDemValue: fnGetPreferredPointDemValue,
    fnScheduleRoute3dSharedSync: function () { return fnScheduleRoute3dSharedSync() },
    fnRefreshWaypointMarker: fnRefreshWaypointMarker,
    fnBuildPointCoordinatesText: fnBuildPointCoordinatesText,
    fnTemplateSupportsPointCoordinatesField: fnTemplateSupportsPointCoordinatesField,
    fnUpdatePointXmlByPath: fnUpdatePointXmlByPath,
    clamp,
    fnEnsureKmzTimestampsOnMissionLoad: fnEnsureKmzTimestampsOnMissionLoad,
    fnSetWaypointDragging: fnSetWaypointDragging,
    fnSchedulePersistImportDraft: fnSchedulePersistImportDraft,
    fnClearPersistedImportDraft: fnClearPersistedImportDraft,
    fnNormalizeStateSnapshot: fnNormalizeStateSnapshot,
    fnParseKmzMission: fnParseKmzMission,
    fnParseKmlMission: fnParseKmlMission,
    fnNormalizeHistoryStack: fnNormalizeHistoryStack,
    fnNormalizeKmzMissionForExport: fnNormalizeKmzMissionForExport,
    fnUpdateMissionLineCoords: fnUpdateMissionLineCoords,
    fnBuildKmzCanonicalExportDocs: fnBuildKmzCanonicalExportDocs
  }
}

const fnRenderImportLines = function (importLines, waypoints, labelPrefix, meta) {
  return renderImportLinesRaw(importLines, waypoints, labelPrefix, meta, fnBuildMissionRenderContext())
}

const fnStyle = function (type, row) {
  if (typeof buildLayerStyle === 'function') {
    return buildLayerStyle(type, row)
  }
  return {
    color: 'rgba(0,0,0,1.0)',
    fillColor: 'rgba(100,100,100,1.0)',
    weight: 1,
    dashOffset: 0,
    opacity: 0.6,
    fillOpacity: 0.2
  }
}

const layerParsersController = createLayerParsers({
  $: $,
  _: _,
  momentRef: moment,
  windowRef: window,
  fnT: fnT,
  fnStyle: fnStyle,
  fnCordIsOnMap: fnCordIsOnMap,
  fnLayerBuilder: fnLayerBuilder,
  fnIsValidLayerCord: fnIsValidLayerCord,
  aDMS_aDDT: aDMS_aDDT,
  sDMS_aDDT: sDMS_aDDT,
  sDMS_nDDT: sDMS_nDDT,
  sDMSC_nDDT: sDMSC_nDDT
})
const fnLayerNotams = function (res) {
  return layerParsersController.layerNotams(res)
}
const fnLayerAirSpaces = function (res) {
  return layerParsersController.layerAirSpaces(res)
}
const fnLayerAirPorts = function (res) {
  return layerParsersController.layerAirPorts(res)
}
const fnLayerNature = function (res) {
  return layerParsersController.layerNature(res)
}
const fnLayerUser = function (res) {
  return layerParsersController.layerUser(res)
}
const fnVersions = function (res) {
  layerVersions = layerParsersController.collectVersions(res)
  return layerVersions
}

let layerVersions = {}
const fnRenderReadmePanel = function ($container, payload) {
  return renderReadmePanel($container, payload, { t: fnT, $: $ })
}

const portalInfoController = createPortalInfoController({
  $: $,
  $body: $body,
  baseSwal: baseSwal,
  fnT: fnT,
  fetchImpl: fetch,
  windowRef: window
})
const fnFetchReadmeDocs = function (forceRefresh) {
  return portalInfoController.fetchReadmeDocs(forceRefresh)
}
const fnCheckForAppVersionUpdate = function () {
  return portalInfoController.checkForAppVersionUpdate()
}
const fnInitAppVersionWatcher = function () {
  return portalInfoController.initAppVersionWatcher()
}
const fnHasAppVersionUpdate = function () {
  return portalInfoController.hasAppVersionUpdate()
}
const fnInitVersionDebugApi = function () {
  return portalInfoController.initVersionDebugApi()
}
const fnInitElectronAutoUpdate = function () {
  return portalInfoController.initElectronAutoUpdate()
}
const fnInitElectronDownloadAction = function () {
  return portalInfoController.initElectronDownloadAction()
}

const layerLoaderController = createLayerLoaderController({
  $: $,
  $body: $body,
  windowRef: window,
  fnT: fnT,
  fnGetDb: function () { return db },
  store: store,
  areaSwal: areaSwal,
  fnLayerNotams: fnLayerNotams,
  fnLayerAirSpaces: fnLayerAirSpaces,
  fnLayerAirPorts: fnLayerAirPorts,
  fnLayerNature: fnLayerNature,
  fnLayerUser: fnLayerUser,
  fnVersions: fnVersions,
  fnLayerFilterAll: fnLayerFilterAll,
  fnGetMap: function () { return map },
  fnResetLayersOnMap: function () { return layerCoreController.resetLayersOnMap() }
})
layerLoaderController.bindLayerControlEvents()
const fnCollectLayers = function () {
  return layerLoaderController.collectLayers()
}
const fnGetLayers = function (filter) {
  return layerLoaderController.getLayers(filter)
}

const fnGetGeoTiffResolution = function (sizeBytes) {
  const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1))
  if (!(sizeBytes > 0)) return Math.round(256 * dpr)
  if (sizeBytes > (2 * 1024 * 1024 * 1024)) return Math.round(96 * dpr)
  if (sizeBytes > (1024 * 1024 * 1024)) return Math.round(128 * dpr)
  if (sizeBytes > (256 * 1024 * 1024)) return Math.round(192 * dpr)
  return Math.round(256 * dpr)
}

const fnClampGeoTiffResolution = function (value) {
  const min = 16
  const max = 512
  const step = 16
  const raw = parseInt(String(value || ''), 10)
  if (!Number.isFinite(raw)) return 256
  const clamped = Math.max(min, Math.min(max, raw))
  return Math.round(clamped / step) * step
}

const fnClearGeoTiffLayer = function (sourceType) {
  const source = String(sourceType || '').trim().toLowerCase()
  if (source === 'terrain') {
    if (tiffDemItems) tiffDemItems.clearLayers()
    tiffLayerBySource.terrain = null
    return
  }
  if (source === 'ortho') {
    if (tiffOrthoItems) tiffOrthoItems.clearLayers()
    tiffLayerBySource.ortho = null
    return
  }
  if (tiffDemItems) tiffDemItems.clearLayers()
  if (tiffOrthoItems) tiffOrthoItems.clearLayers()
  tiffLayerBySource = { terrain: null, ortho: null }
}

const fnDropGeoTiffRuntimeCache = function () {
  tiffParsedMemoryCacheBySource = {
    terrain: {
      signature: null,
      georaster: null
    },
    ortho: {
      signature: null,
      georaster: null
    }
  }
}

const fnGeoTiffLog = function () {
  if (!GEOTIFF_DEBUG_LOG) return
  const args = Array.from(arguments || [])
  console.log.apply(console, ['[GeoTIFF]'].concat(args))
}

const fnReadFileAsArrayBufferWithProgress = function (file, onProgress) {
  return new Promise(function (resolve, reject) {
    const reader = new FileReader()
    reader.onerror = function () {
      reject(reader.error || new Error(fnT('log.file_reader_error')))
    }
    reader.onprogress = function (event) {
      if (typeof onProgress === 'function') {
        onProgress({
          loaded: event.loaded || 0,
          total: event.total || file.size || 0,
          lengthComputable: !!event.lengthComputable
        })
      }
    }
    reader.onload = function () {
      resolve(reader.result)
    }
    reader.readAsArrayBuffer(file)
  })
}

const fnGeoTiffFileSignature = function (file) {
  if (!file) return ''
  const name = String(file.name || '')
  const size = Number(file.size || 0)
  const modified = Number(file.lastModified || 0)
  return name + '|' + size + '|' + modified
}

const fnGeoTiffParseCacheMissReason = function (signature) {
  if (!signature) return fnT('log.geotiff_parse_reason_missing_signature')
  const source = String(signature).startsWith('ortho::') ? 'ortho' : 'terrain'
  const parsedCache = tiffParsedMemoryCacheBySource[source] || { signature: null, georaster: null }
  if (!parsedCache || !parsedCache.signature) return fnT('log.geotiff_parse_reason_empty_memory_cache')
  if (parsedCache.signature !== signature) return fnT('log.geotiff_parse_reason_signature_mismatch')
  if (!parsedCache.georaster) return fnT('log.geotiff_parse_reason_missing_georaster')
  return fnT('common.unknown')
}

const fnGeoTiffCacheDataKey = function (signature) {
  return cacheDataKey(signature, {
    GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
  })
}

const fnRefreshGeoTiffCacheMaxBytes = async function () {
  geotiffCacheMaxBytes = await refreshGeoTiffCacheMaxBytes({
    fnT: fnT,
    fnGeoTiffLog: fnGeoTiffLog,
    GEOTIFF_CACHE_MIN_BYTES: GEOTIFF_CACHE_MIN_BYTES,
    GEOTIFF_CACHE_HARD_MAX_BYTES: GEOTIFF_CACHE_HARD_MAX_BYTES,
    GEOTIFF_CACHE_FALLBACK_MAX_BYTES: GEOTIFF_CACHE_FALLBACK_MAX_BYTES,
    navigatorRef: navigator
  })
  return geotiffCacheMaxBytes
}

const fnGeoTiffCacheReadIndex = async function () {
  return cacheReadIndex({
    idbKeyval: window.idbKeyval,
    fnT: fnT,
    GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY
  })
}

const fnGeoTiffCacheWriteIndex = async function (index) {
  return cacheWriteIndex(index, {
    idbKeyval: window.idbKeyval,
    fnT: fnT,
    GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY
  })
}

const fnGeoTiffCacheTouch = async function (signature) {
  return cacheTouch(signature, {
    idbKeyval: window.idbKeyval,
    fnT: fnT,
    GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY
  })
}

const fnGeoTiffCacheLoad = async function (file, providedSignature, cacheOptions) {
  const opts = (cacheOptions && typeof cacheOptions === 'object') ? cacheOptions : {}
  return cacheLoad(file, {
    idbKeyval: window.idbKeyval,
    fnT: fnT,
    fnGeoTiffLog: fnGeoTiffLog,
    fnGeoTiffFileSignature: function (nextFile) { return String(providedSignature || fnGeoTiffFileSignature(nextFile) || '') },
    GEOTIFF_CACHE_KEY_PREFIX: String(opts.GEOTIFF_CACHE_KEY_PREFIX || GEOTIFF_CACHE_KEY_PREFIX),
    GEOTIFF_CACHE_INDEX_KEY: String(opts.GEOTIFF_CACHE_INDEX_KEY || GEOTIFF_CACHE_INDEX_KEY)
  })
}

const fnGeoTiffCacheStore = async function (file, arrayBuffer, providedSignature, cacheOptions) {
  const opts = (cacheOptions && typeof cacheOptions === 'object') ? cacheOptions : {}
  return cacheStore(file, arrayBuffer, {
    idbKeyval: window.idbKeyval,
    fnT: fnT,
    fnGeoTiffLog: fnGeoTiffLog,
    fnGeoTiffFileSignature: function (nextFile) { return String(providedSignature || fnGeoTiffFileSignature(nextFile) || '') },
    GEOTIFF_CACHE_KEY_PREFIX: String(opts.GEOTIFF_CACHE_KEY_PREFIX || GEOTIFF_CACHE_KEY_PREFIX),
    GEOTIFF_CACHE_INDEX_KEY: String(opts.GEOTIFF_CACHE_INDEX_KEY || GEOTIFF_CACHE_INDEX_KEY),
    GEOTIFF_CACHE_MIN_BYTES: GEOTIFF_CACHE_MIN_BYTES,
    GEOTIFF_CACHE_HARD_MAX_BYTES: GEOTIFF_CACHE_HARD_MAX_BYTES,
    GEOTIFF_CACHE_FALLBACK_MAX_BYTES: GEOTIFF_CACHE_FALLBACK_MAX_BYTES,
    navigatorRef: navigator
  })
}

void fnRefreshGeoTiffCacheMaxBytes()

const fnLoadGeoTiffFile = async function (file, options) {
  return loadGeoTiffFile(file, options, {
    _: _,
    $: $,
    Swal: Swal,
    map: map,
    tiffItems: {
      terrain: tiffDemItems,
      ortho: tiffOrthoItems
    },
    db: db,
    fnSetTiffLayer: function (nextLayer, sourceType) {
      const source = String(sourceType || '').trim().toLowerCase() === 'ortho' ? 'ortho' : 'terrain'
      tiffLayerBySource[source] = nextLayer || null
    },
    fnSetTiffLastFile: function (nextFile, sourceType) {
      const source = String(sourceType || '').trim().toLowerCase() === 'ortho' ? 'ortho' : 'terrain'
      tiffLastFileBySource[source] = nextFile || null
    },
    fnGetTiffParsedMemoryCache: function (sourceType) {
      const source = String(sourceType || '').trim().toLowerCase() === 'ortho' ? 'ortho' : 'terrain'
      return tiffParsedMemoryCacheBySource[source] || { signature: null, georaster: null }
    },
    fnSetTiffParsedMemoryCache: function (nextCache, sourceType) {
      const source = String(sourceType || '').trim().toLowerCase() === 'ortho' ? 'ortho' : 'terrain'
      tiffParsedMemoryCacheBySource[source] = nextCache || { signature: null, georaster: null }
    },
    fnT: fnT,
    fnTF: fnTF,
    fnClampGeoTiffResolution: fnClampGeoTiffResolution,
    fnGetGeoTiffResolution: fnGetGeoTiffResolution,
    fnGeoTiffLog: fnGeoTiffLog,
    fnClearGeoTiffLayer: fnClearGeoTiffLayer,
    fnGeoTiffCacheLoad: fnGeoTiffCacheLoad,
    fnGeoTiffCacheReadIndex: fnGeoTiffCacheReadIndex,
    fnGeoTiffCacheWriteIndex: fnGeoTiffCacheWriteIndex,
    fnReadFileAsArrayBufferWithProgress: fnReadFileAsArrayBufferWithProgress,
    fnGeoTiffFileSignature: fnGeoTiffFileSignature,
    fnGeoTiffParseCacheMissReason: fnGeoTiffParseCacheMissReason,
    fnGeoTiffCacheStore: fnGeoTiffCacheStore,
    fnDropGeoTiffRuntimeCache: fnDropGeoTiffRuntimeCache,
    GEOTIFF_ALLOWED_MIME_TYPES: GEOTIFF_ALLOWED_MIME_TYPES,
    GEOTIFF_MAX_FILE_BYTES: GEOTIFF_MAX_FILE_BYTES,
    GEOTIFF_DEBUG_LOG: GEOTIFF_DEBUG_LOG
  })
}

const fnFetchSurfaceElevationByProvider = async function (providerKey, batchPoints, options) {
  return fetchSurfaceElevationByProvider(providerKey, batchPoints, {
    ...(options && typeof options === 'object' ? options : {}),
    fnIsDemProviderEnabled: fnIsDemProviderEnabled,
    fnT: fnT,
    fetchImpl: fetch,
    DEM_PROVIDER_PRIORITY: DEM_PROVIDER_PRIORITY,
    localFallbackProviderKey: mapCursorElevationController.getFallbackProvider()
  })
}

const fnSyncMissionPlacemarkOrder = function () {
  if (!importMissionState || !Array.isArray(importMissionState.waypoints)) return
  if (importMissionType === 'kmz') {
    importMissionState.waypoints.forEach((pt, index) => {
      if (!pt) return
      pt.idx = index
      const currentWpmlIndex = _.get(pt, 'props.wpml:index')
      const currentIndex = _.get(pt, 'props.index')
      if (String(currentWpmlIndex) !== String(index)) {
        fnSetByPath(pt.props, 'wpml:index', index)
        fnUpdatePointXmlByPath(pt, 'wpml:index', index)
      }
      if (String(currentIndex) !== String(index)) {
        fnSetByPath(pt.props, 'index', index)
        fnUpdatePointXmlByPath(pt, 'index', index)
      }
    })
  }
  if (importMissionState.folderEl) {
    fnSyncPlacemarkOrder(importMissionState.folderEl, importMissionState.waypoints)
  }
  if (importMissionState.secondaryFolderEl) {
    const secondaryPoints = importMissionState.waypoints.map((pt) => {
      if (!pt || !Array.isArray(pt.linkedXmlEls) || !pt.linkedXmlEls.length) return null
      return { xmlEl: pt.linkedXmlEls[0] }
    }).filter(Boolean)
    fnSyncPlacemarkOrder(importMissionState.secondaryFolderEl, secondaryPoints)
  }
}

const fnGetDemProviderLabel = function (providerKey) {
  return mapCursorElevationController.getDemProviderLabel(providerKey)
}
const fnGetMapCursorElevationProvider = function () {
  return mapCursorElevationController.getProvider()
}

const fnSetMapCursorElevationProvider = function (providerKey, options) {
  return mapCursorElevationController.setProvider(providerKey, options)
}

const fnBuildMapCursorElevationTryOrder = function (primaryProvider) {
  return mapCursorElevationController.buildTryOrder(primaryProvider)
}

const mapCursorElevationController = createMapCursorElevationController({
  $: $,
  fnGetMap: function () { return map },
  db: db,
  store: store,
  baseSwal: baseSwal,
  fnT: fnT,
  fnTF: fnTF,
  fnIsDemProviderEnabled: fnIsDemProviderEnabled,
  DEM_PROVIDER_CONFIG: DEM_PROVIDER_CONFIG,
  DEM_CURSOR_PROVIDER_PRIORITY: DEM_CURSOR_PROVIDER_PRIORITY,
  DEM_PROVIDER_PRIORITY: DEM_PROVIDER_PRIORITY,
  DEM_PROVIDER_G: DEM_PROVIDER_G,
  MAP_CURSOR_ELEVATION_CACHE_MAX: MAP_CURSOR_ELEVATION_CACHE_MAX,
  MAP_CURSOR_ELEVATION_COORD_DECIMALS: MAP_CURSOR_ELEVATION_COORD_DECIMALS,
  MAP_CURSOR_ELEVATION_MIN_REQUEST_INTERVAL_MS: MAP_CURSOR_ELEVATION_MIN_REQUEST_INTERVAL_MS,
  toFiniteNumber: toFiniteNumber,
  fnFetchSurfaceElevationByProvider: fnFetchSurfaceElevationByProvider
})

const fnInitMapCursorElevationTracking = function () {
  return mapCursorElevationController.initTracking()
}

const fnOpenMapCursorElevationSettingsModal = function () {
  return openMapCursorElevationSettingsModal()
}

const demBatchController = createDemBatchController({
  _: _,
  $: $,
  Swal: Swal,
  areaSwal: areaSwal,
  fnT: fnT,
  fnTF: fnTF,
  POINT_ALTITUDE_MODE_VALUES: POINT_ALTITUDE_MODE_VALUES,
  DEM_PROVIDER_CONFIG: DEM_PROVIDER_CONFIG,
  DEM_PROVIDER_PRIORITY: DEM_PROVIDER_PRIORITY,
  fnIsDemProviderEnabled: fnIsDemProviderEnabled,
  fnGetMissionState: function () { return importMissionState },
  fnBuildDemLoadingPopupHtml: fnBuildDemLoadingPopupHtml,
  fnCaptureState: fnCaptureState,
  fnGetDemProviderLabel: fnGetDemProviderLabel,
  fnFetchSurfaceElevationByProvider: fnFetchSurfaceElevationByProvider,
  fnSetPointDemValue: fnSetPointDemValue,
  fnBuildPointCoordinatesText: fnBuildPointCoordinatesText,
  fnTemplateSupportsPointCoordinatesField: fnTemplateSupportsPointCoordinatesField,
  fnSetByPath: fnSetByPath,
  fnUpdatePointXmlByPath: fnUpdatePointXmlByPath,
  fnSetPointAglValue: fnSetPointAglValue,
  fnSetPointDescription: fnSetPointDescription,
  fnBuildAglLine: fnBuildAglLine,
  fnBuildDemLine: fnBuildDemLine,
  fnGetWaypointTooltipText: fnGetWaypointTooltipText,
  fnUpdateMissionLineCoords: fnUpdateMissionLineCoords,
  fnScheduleRoute3dSharedSync: fnScheduleRoute3dSharedSync,
  fnHistoryPush: fnHistoryPush
})

const fnApplyDemProviderToMissionPoints = async function (providerKey, options) {
  return demBatchController.applyProviderToMissionPoints(providerKey, options)
}

const fnApplyAllDemProvidersToMissionPoints = async function () {
  return demBatchController.applyAllProvidersToMissionPoints()
}

const fnGetPointHeightToolFieldMeta = function (targetPath) {
  const lower = String(targetPath || '').toLowerCase()
  if (!lower) return null
  if (lower.includes('executeheight')) {
    return {
      leaf: 'executeHeight',
      aliases: POINT_EXECUTE_HEIGHT_PATH_ALIASES
    }
  }
  if (lower.includes('height')) {
    return {
      leaf: 'height',
      aliases: POINT_HEIGHT_PATH_ALIASES
    }
  }
  return null
}

const fnEscapeHtmlLite = function (raw) {
  return escapeHtmlLite(raw)
}

const fnRunPointHeightDemOverwriteFlow = async function (options) {
  return runPointHeightDemOverwriteFlow(options, {
    _: _,
    $: $,
    Swal: Swal,
    areaSwal: areaSwal,
    fnGetPointHeightToolFieldMeta: fnGetPointHeightToolFieldMeta,
    fnIsDemProviderEnabled: fnIsDemProviderEnabled,
    fnT: fnT,
    fnTF: fnTF,
    toFiniteNumber: toFiniteNumber,
    NON_EXPORT_POINT_HEIGHT_PATH: NON_EXPORT_POINT_HEIGHT_PATH,
    fnGetPointNumericByPathAliases: fnGetPointNumericByPathAliases,
    fnGetDemProviderLabel: fnGetDemProviderLabel,
    fnFetchSurfaceElevationByProvider: fnFetchSurfaceElevationByProvider,
    DEM_PROVIDER_CONFIG: DEM_PROVIDER_CONFIG,
    fnSetPointDemValue: fnSetPointDemValue,
    fnApplyEditsToPoints: fnApplyEditsToPoints
  })
}

bindCoreUiHandlers({
  _: _,
  $: $,
  $body: $body,
  $leafletmap: $leafletmap,
  map: map,
  db: db,
  store: store,
  Swal: Swal,
  areaSwal: areaSwal,
  fnClampGeoTiffResolution: fnClampGeoTiffResolution,
  fnGetGeoTiffResolution: fnGetGeoTiffResolution,
  fnT: fnT,
  fnTF: fnTF,
  fnLoadGeoTiffFile: fnLoadGeoTiffFile,
  fnSetMyLocationButtonActive: fnSetMyLocationButtonActive,
  fnClearMyLocationLocateTimer: fnClearMyLocationLocateTimer,
  fnTryBrowserMyLocationFallback: fnTryBrowserMyLocationFallback,
  fnLayerFilterAll: fnLayerFilterAll,
  fnGetLayers: fnGetLayers,
  fnClearGeoTiffLayer: fnClearGeoTiffLayer,
  fnGetTiffLastFile: function (sourceType) {
    const source = String(sourceType || '').trim().toLowerCase() === 'ortho' ? 'ortho' : 'terrain'
    return tiffLastFileBySource[source] || null
  },
  fnSetTiffLastFile: function (nextFile, sourceType) {
    const source = String(sourceType || '').trim().toLowerCase() === 'ortho' ? 'ortho' : 'terrain'
    tiffLastFileBySource[source] = nextFile || null
  },
  fnGetMyLocationActive: function () { return myLocationController.isActive() },
  fnSetMyLocationFallbackTried: function (nextValue) { myLocationController.setFallbackTried(nextValue) },
  fnGetMyLocationMarker: function () { return myLocationController.getMarker() },
  fnSetMyLocationMarker: function (nextMarker) { myLocationController.setMarker(nextMarker) },
  fnSetMyLocationLocateTimer: function (nextTimer) { myLocationController.setLocateTimer(nextTimer) }
})

const fnSetMissionWaypointLatLng = function (point, lat, lng) {
  return setMissionWaypointLatLngRaw(point, lat, lng, fnBuildMissionRenderContext())
}

const fnPlaceMissionWaypointsInViewport = function (parsedMission) {
  return placeMissionWaypointsInViewportRaw(parsedMission, fnBuildMissionRenderContext())
}

const fnLoadMissionFromParsedMission = function (parsedMission, filename, options) {
  return loadMissionFromParsedMissionRaw(parsedMission, filename, options, fnBuildMissionRenderContext())
}

const fnCloseEditedMission = function () {
  return closeEditedMissionRaw(fnBuildMissionRenderContext())
}

const fnCloseMissionVariants = function (variantKeys) {
  return closeMissionVariantsRaw(variantKeys, fnBuildMissionRenderContext())
}

const fnRestoreImportDraftFromIndexedDb = async function () {
  return restoreImportDraftFromIndexedDbRaw(fnBuildMissionRenderContext())
}

void fnRestoreImportDraftFromIndexedDb()

const fnExportCurrentMission = function (targetType) {
  return exportCurrentMissionRaw(targetType, fnBuildMissionRenderContext())
}

const route3dSyncController = createRoute3dSyncController({
  _: _,
  fnT: fnT,
  toFiniteNumber: toFiniteNumber,
  fnGetMissionState: function () { return importMissionState },
  fnGetMissionFileName: function () { return importKmlFilename },
  fnGetMissionType: function () { return importMissionType },
  fnGetMissionAltitudeContextFor3d: fnGetMissionAltitudeContextFor3d,
  fnGetPreferredPointDemValue: fnGetPreferredPointDemValue,
  fnGetPointAltitudeFor3d: fnGetPointAltitudeFor3d,
  fnGetPointRawHeightValue: fnGetPointRawHeightValue,
  fnGetPointName: fnGetPointName,
  fnGetPointDescription: fnGetPointDescription,
  Swal: Swal,
  windowRef: window,
  idbKeyval: window.idbKeyval
})
const fnBuildRoute3dPayloadFromMission = function () {
  return route3dSyncController.buildPayloadFromMission()
}
const fnBuildRoute3dPayloadSignature = function (payload) {
  return route3dSyncController.buildPayloadSignature(payload)
}
const fnSaveRoute3dPayloadToIndexedDb = async function (payload) {
  return route3dSyncController.savePayloadToIndexedDb(payload)
}
const fnSyncRoute3dSharedState = function () {
  return route3dSyncController.syncSharedState()
}
fnScheduleRoute3dSharedSync = function () {
  return route3dSyncController.scheduleSharedSync()
}
const fnOpenRoute3DViewer = function () {
  return route3dSyncController.openViewer()
}

bindMissionToolbarHandlers({
  $: $,
  _: _,
  L: L,
  $body: $body,
  Swal: Swal,
  areaSwal: areaSwal,
  momentRef: moment,
  windowRef: window,
  fnT: fnT,
  fnTF: fnTF,
  fnGetMap: function () { return map },
  fnGetMissionState: function () { return importMissionState },
  fnGetImportItems: function () { return importItems },
  fnGetImportLineItems: function () { return importLineItems },
  fnSetImportKmlFilename: function (nextFileName) { importKmlFilename = nextFileName },
  fnParseKmlMission: fnParseKmlMission,
  fnParseKmzMission: fnParseKmzMission,
  fnApplyKmzMissionTimestamps: fnApplyKmzMissionTimestamps,
  fnPlaceMissionWaypointsInViewport: fnPlaceMissionWaypointsInViewport,
  fnRenameWaypointsLinear: fnRenameWaypointsLinear,
  buildLocationBasedMissionBaseName: buildLocationBasedMissionBaseName,
  fnLoadMissionFromParsedMission: fnLoadMissionFromParsedMission,
  fnOpenProjectSettingsEditor: fnOpenProjectSettingsEditor,
  fnExportCurrentMission: fnExportCurrentMission,
  fnOpenRoute3DViewer: fnOpenRoute3DViewer,
  fnCloseMissionVariants: fnCloseMissionVariants,
  fnCloseEditedMission: fnCloseEditedMission,
  KML_MAX_FILE_BYTES: KML_MAX_FILE_BYTES,
  KMZ_MAX_FILE_BYTES: KMZ_MAX_FILE_BYTES
})
bindWaypointToolsHandlers({
  _: _,
  $: $,
  L: L,
  $body: $body,
  Swal: Swal,
  areaSwal: areaSwal,
  fnT: fnT,
  state: {
    get map () { return map },
    get importMissionState () { return importMissionState },
    set importMissionState (nextValue) { importMissionState = nextValue },
    get importSelectedPoints () { return importSelectedPoints },
    set importSelectedPoints (nextValue) { importSelectedPoints = nextValue },
    get importLineItems () { return importLineItems },
    set importLineItems (nextValue) { importLineItems = nextValue },
    get importKmlFilename () { return importKmlFilename },
    set importKmlFilename (nextValue) { importKmlFilename = nextValue },
    get importSelectMode () { return importSelectMode },
    set importSelectMode (nextValue) { importSelectMode = !!nextValue },
    get importAddPointMode () { return importAddPointMode },
    set importAddPointMode (nextValue) { importAddPointMode = !!nextValue },
    get importDragMode () { return importDragMode },
    set importDragMode (nextValue) { importDragMode = !!nextValue },
    get importActivePoint () { return importActivePoint },
    set importActivePoint (nextValue) { importActivePoint = nextValue || null },
    get importSelectDrawRect () { return importSelectDrawRect },
    set importSelectDrawRect (nextValue) { importSelectDrawRect = nextValue || null },
    get importSelectDrawPoly () { return importSelectDrawPoly },
    set importSelectDrawPoly (nextValue) { importSelectDrawPoly = nextValue || null },
    get importDivideNameRound () { return importDivideNameRound },
    set importDivideNameRound (nextValue) { importDivideNameRound = Number(nextValue) || 0 }
  },
  DEM_PROVIDER_PRIORITY: DEM_PROVIDER_PRIORITY,
  POINT_HEIGHT_PATH_ALIASES: POINT_HEIGHT_PATH_ALIASES,
  POINT_EXECUTE_HEIGHT_PATH_ALIASES: POINT_EXECUTE_HEIGHT_PATH_ALIASES,
  NON_EXPORT_POINT_HEIGHT_PATH: NON_EXPORT_POINT_HEIGHT_PATH,
  roundToTenth: roundToTenth,
  toFiniteNumber: toFiniteNumber,
  fnSyncAddPointCursor: fnSyncAddPointCursor,
  fnSetWaypointDragging: fnSetWaypointDragging,
  fnSelectAllPoints: fnSelectAllPoints,
  fnSelectClear: fnSelectClear,
  fnBuildDividePointsPopupHtml: fnBuildDividePointsPopupHtml,
  fnIsDemProviderEnabled: fnIsDemProviderEnabled,
  fnGetDemProviderLabel: fnGetDemProviderLabel,
  fnCaptureState: fnCaptureState,
  fnGetPointName: fnGetPointName,
  fnRemovePointXmlNodes: fnRemovePointXmlNodes,
  fnSyncMissionPlacemarkOrder: fnSyncMissionPlacemarkOrder,
  fnRenderImportLines: fnRenderImportLines,
  fnHistoryPush: fnHistoryPush,
  fnGetPointHeading: fnGetPointHeading,
  fnGetFirstNumericFromPointXmlTags: fnGetFirstNumericFromPointXmlTags,
  fnGetPointRawHeightValue: fnGetPointRawHeightValue,
  fnRoundLetter: fnRoundLetter,
  fnCreateInsertedPoint: fnCreateInsertedPoint,
  fnSetByPath: fnSetByPath,
  fnSetPointValueByPathAliases: fnSetPointValueByPathAliases,
  fnSetPointDemValue: fnSetPointDemValue,
  fnBuildDivideRenameRowsFromCreatedPoints: fnBuildDivideRenameRowsFromCreatedPoints,
  fnApplyDivideRowNames: fnApplyDivideRowNames,
  fnFetchSurfaceElevationByProvider: fnFetchSurfaceElevationByProvider,
  fnSwalBulkEdit: fnSwalBulkEdit
})

bindAltitudeUiHandlers({
  $: $,
  $body: $body,
  Swal: Swal,
  areaSwal: areaSwal,
  fnT: fnT,
  fnBuildMapFunctionsPopupHtml: fnBuildMapFunctionsPopupHtml,
  fnGetMissionState: function () { return importMissionState },
  fnIsDemProviderEnabled: fnIsDemProviderEnabled,
  DEM_PROVIDER_PRIORITY: DEM_PROVIDER_PRIORITY,
  fnApplyAllDemProvidersToMissionPoints: fnApplyAllDemProvidersToMissionPoints,
  fnApplyDemProviderToMissionPoints: fnApplyDemProviderToMissionPoints
})

bindSeekUiHandlers({
  _: _,
  $: $,
  L: L,
  $body: $body,
  Swal: Swal,
  areaSwal: areaSwal,
  fnT: fnT,
  fnTF: fnTF,
  fnGetMissionState: function () { return importMissionState },
  fnGetMap: function () { return map },
  fnGetDrawnItems: function () { return drawnItems },
  fnGetSeekDrawActive: function () { return importSeekDrawActive },
  fnSetSeekMode: function (nextValue) { importSeekMode = !!nextValue },
  fnSetSeekDrawActive: function (nextValue) { importSeekDrawActive = !!nextValue },
  fnSetSeekDrawLine: function (nextValue) { importSeekDrawLine = nextValue || null },
  fnSetSeekAxisCallback: function (nextValue) { importSeekAxisCallback = nextValue || null },
  fnSetSeekAxisUpBearingLast: function (nextValue) { importSeekAxisUpBearingLast = nextValue },
  fnResolveSeekAxisUpBearing: fnResolveSeekAxisUpBearing,
  fnBuildSeekRowCandidates: fnBuildSeekRowCandidates,
  fnPickSeekRowCandidate: fnPickSeekRowCandidate,
  fnEstimateSeekCloneDistance: fnEstimateSeekCloneDistance,
  fnCloneSeekRow: fnCloneSeekRow,
  fnCreateSeekAxisLayer: fnCreateSeekAxisLayer,
  fnGetImportWaypointsCenter: fnGetImportWaypointsCenter,
  fnCaptureState: fnCaptureState,
  fnUpdatePointCoordinates: fnUpdatePointCoordinates,
  fnBuildPointCoordinatesText: fnBuildPointCoordinatesText,
  fnTemplateSupportsPointCoordinatesField: fnTemplateSupportsPointCoordinatesField,
  fnUpdatePointXmlByPath: fnUpdatePointXmlByPath,
  fnRefreshImportLine: fnRefreshImportLine,
  fnHistoryPush: fnHistoryPush,
  roundToTenth: roundToTenth,
  normalizeBearingDeg: normalizeBearingDeg,
  offsetLatLngByMeters: offsetLatLngByMeters
})

bindQuickActionHandlers({
  $body: $body,
  fnHistoryUndo: fnHistoryUndo,
  fnHistoryRedo: fnHistoryRedo,
  fnOpenMapCursorElevationSettingsModal: fnOpenMapCursorElevationSettingsModal
})

fnInitMapCursorElevationTracking()

bindInfoModalHandler({
  $: $,
  $body: $body,
  areaSwal: areaSwal,
  fnT: fnT,
  fnCheckForAppVersionUpdate: fnCheckForAppVersionUpdate,
  fnFetchReadmeDocs: fnFetchReadmeDocs,
  fnHasAppVersionUpdate: fnHasAppVersionUpdate,
  fnRenderReadmePanel: fnRenderReadmePanel,
  fnGetLayerVersions: function () { return layerVersions }
})

bindExternalLinkInterceptor({
  $: $,
  $body: $body,
  windowRef: window
})

const fnApplyMod2ShortcutTooltips = function () {
  return applyMod2ShortcutTooltips($body, PORTAL_MOD2_SHORTCUTS, bootstrap)
}

const fnDeactivateMod2Modes = function () {
  return deactivateMod2ModesUi({
    $body: $body,
    fnGetImportAddPointMode: function () { return importAddPointMode },
    fnSetImportAddPointMode: function (nextValue) { importAddPointMode = !!nextValue },
    fnGetImportDragMode: function () { return importDragMode },
    fnSetImportDragMode: function (nextValue) { importDragMode = !!nextValue },
    fnGetImportSelectMode: function () { return importSelectMode },
    fnSetImportSelectMode: function (nextValue) { importSelectMode = !!nextValue },
    fnGetImportActivePoint: function () { return importActivePoint },
    fnSetImportActivePoint: function (nextValue) { importActivePoint = nextValue || null },
    fnGetImportSelectDrawPoly: function () { return importSelectDrawPoly },
    fnGetImportSeekMode: function () { return importSeekMode },
    fnSetImportSeekMode: function (nextValue) { importSeekMode = !!nextValue },
    fnGetImportSeekDrawActive: function () { return importSeekDrawActive },
    fnSetImportSeekDrawActive: function (nextValue) { importSeekDrawActive = !!nextValue },
    fnSetImportSeekAxisCallback: function (nextValue) { importSeekAxisCallback = nextValue || null },
    fnGetImportSeekDrawLine: function () { return importSeekDrawLine },
    fnSetImportSeekDrawLine: function (nextValue) { importSeekDrawLine = nextValue || null },
    fnSyncAddPointCursor: fnSyncAddPointCursor,
    fnSetWaypointDragging: fnSetWaypointDragging
  })
}

const fnInitMod2KeyboardShortcuts = function () {
  return initMod2KeyboardShortcutsUi({
    _: _,
    $: $,
    $body: $body,
    shortcutDefsRaw: PORTAL_MOD2_SHORTCUTS,
    parseShortcut: parseShortcut,
    isTypingContext: isTypingContext,
    normalizeEventKey: normalizeEventKey,
    eventMatchesShortcut: eventMatchesShortcut,
    fnDeactivateMod2Modes: fnDeactivateMod2Modes
  })
}

const fnInitMobileNavbarArrows = function () {
  return initMobileNavbarArrowsUi({
    _: _,
    $: $,
    $body: $body
  })
}
fnInitMobileNavbarArrows()
fnApplyMod2ShortcutTooltips()
fnInitMod2KeyboardShortcuts()
fnInitAppVersionWatcher()
fnInitVersionDebugApi()
fnInitElectronAutoUpdate()
fnInitElectronDownloadAction()

bindBootstrapOverlayUiHandlers({
  $: $,
  $body: $body,
  ensureDropdown: ensureDropdown,
  ensureTooltip: ensureTooltip
})

bindBeforeUnloadCleanupHandler({
  windowRef: window,
  fnGetImportDraftPersistTimer: function () { return importDraftPersistTimer },
  fnSetImportDraftPersistTimer: function (nextTimer) { importDraftPersistTimer = nextTimer || null },
  fnClearGeoTiffLayer: fnClearGeoTiffLayer,
  fnDropGeoTiffRuntimeCache: fnDropGeoTiffRuntimeCache,
  fnSetTiffLastFile: function (nextFile) {
    tiffLastFileBySource.terrain = nextFile || null
    tiffLastFileBySource.ortho = nextFile || null
  }
})

return {
  baseSwal: baseSwal,
  areaSwal: areaSwal,
  fnT: fnT,
  fnIsDemProviderEnabled: fnIsDemProviderEnabled,
  googleElevationApiKey: googleElevationApiKey,
  fnShowPropContextMenu: fnShowPropContextMenu,
  fnResolvePropValueHelp: fnResolvePropValueHelp,
  fnGetDemProviderLabel: fnGetDemProviderLabel,
  fnGetMapCursorElevationProvider: fnGetMapCursorElevationProvider,
  fnSetMapCursorElevationProvider: fnSetMapCursorElevationProvider,
  fnBuildMapCursorElevationTryOrder: fnBuildMapCursorElevationTryOrder,
  fnEscapeHtmlLite: fnEscapeHtmlLite
}
}
